import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { hasPermission } from '@/types';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ocenaRyzyka as api } from '@/api/endpoints';
import type {
  RiskAssessment, RiskAssessmentStatusCode, RiskCatchment, RiskProtectionZone, RiskChecklistItem, RiskEvidenceFact, RiskGap,
  RiskGenerationStage, RiskHazard, RiskControl, RiskMeta, RiskNarrativeSection, RiskPressureOverlay, RiskSourceStatus,
  RiskAssessmentWell, RiskSourceDocument, RiskDocChecklist, RiskDocProposal, RiskDocStatusCode,
  OperationalKindCode,
} from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { FALLBACK_BANDS, RiskMatrix } from '@/components/risk/RiskMatrix';
import { PressureDetailPanel } from '@/components/risk/PressureDetailPanel';
import { OcenaMap } from '@/components/risk/OcenaMap';
import { WellsPanel } from '@/components/risk/WellsPanel';
import { GeoreferencerModal } from '@/components/risk/GeoreferencerModal';
import { CatchmentMapPreview } from '@/components/risk/CatchmentMapPreview';
import { TiptapEditor } from '@/components/TiptapEditor';
import { markdownToHtml } from '@/components/MarkdownView';
import { useAuth } from '@/auth/AuthContext';
import { validateUploadSize, fmtBytes } from '@/lib/uploadLimits';
import { UploadProgressBar } from '@/components/UploadProgress';
import { readDroppedFiles, isSystemJunk } from '@/components/dropFiles';

type Tab = 'overview' | 'input' | 'sourceDocs' | 'hazards' | 'monitoring' | 'document';
/** Klucze zakładek w URL (?tab=…) — dzięki temu odświeżenie strony wraca na tę samą zakładkę (uwaga 16). */
const TAB_KEYS: Tab[] = ['overview', 'input', 'sourceDocs', 'hazards', 'monitoring', 'document'];
function parseTab(v: string | null): Tab {
  return TAB_KEYS.includes(v as Tab) ? (v as Tab) : 'overview';
}

/**
 * Zakładka otwierana przy wejściu na kartę bez `?tab=` (uwagi 7 i 15): pierwszy nieukończony krok
 * procesu — najpierw dokumenty źródłowe, z których AI czyta dane, potem dane wejściowe, na końcu
 * przegląd. Żadna zakładka NIE jest blokowana; to podpowiedź kolejności, nie bramka.
 */
function firstIncompleteTab(ra: RiskAssessment, docsCount: number): Tab {
  if (ra.status === 'PODPISANY' || ra.status === 'PRZEKAZANY') return 'document';
  if (docsCount === 0) return 'sourceDocs';
  if (!ra.inputData?.complete) return 'input';
  return 'overview';
}

const STEPS = ['Przyjęcie', 'Dane · walidacja', 'Szkic', 'Recenzja', 'Podpis', 'Przekazanie'];
function stepIndex(s: RiskAssessmentStatusCode): number {
  switch (s) {
    case 'NOWE': return 0;
    case 'OCZEKUJE_NA_UZUPELNIENIE': case 'W_KOLEJCE': return 1;
    case 'W_PRZYGOTOWANIU': case 'DO_WERYFIKACJI': case 'ZWROCONY': return 2;
    case 'DO_RECENZJI': case 'RECENZJA': return 3;
    case 'PODPISANY': return 4;
    case 'PRZEKAZANY': return 5;
    default: return 0;
  }
}

export function OcenaRyzykaDetailPage() {
  const { id } = useParams();
  const raId = Number(id);
  const navigate = useNavigate();
  const { user } = useAuth();

  const [ra, setRa] = useState<RiskAssessment | null>(null);
  const [meta, setMeta] = useState<RiskMeta | null>(null);
  // zakładka trzymana w URL, nie w stanie — odświeżenie strony i link wysłany koledze trafiają
  // w to samo miejsce; `replace` nie zaśmieca historii przeglądarki każdym kliknięciem zakładki
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  // wejście bez ?tab= ląduje na pierwszym nieukończonym kroku (uwaga 7) — liczone RAZ, po wczytaniu
  // stanu; późniejsze uzupełnienie danych nie wyrywa użytkownika z zakładki, na której pracuje
  const [autoTab, setAutoTab] = useState<Tab | null>(null);
  const tab = tabParam ? parseTab(tabParam) : (autoTab ?? 'overview');
  const setTab = (t: Tab) => {
    const p = new URLSearchParams(searchParams);
    p.set('tab', t);
    setSearchParams(p, { replace: true });
  };
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const canEdit = has('RISK_EDIT');
  const canSign = has('RISK_SIGN');
  const canAI = has('AI_GENERATE');
  const canAssign = has('RISK_ASSIGN');
  // import ręczny warstwy presji podmienia GLOBALNĄ warstwę — bramka administracyjna, nie RISK_EDIT
  const canUpload = has('ADMIN_CONFIG');
  // Wyłącznie macierz — wcześniejsze „albo OWNER/ADMIN" sprawiało, że odebranie RISK_* tym
  // rolom nic nie dawało, a rola utworzona w macierzy z tymi uprawnieniami i tak nie widziała akcji.
  function has(p: string) {
    return hasPermission(user, p);
  }

  const [catchment, setCatchment] = useState<RiskCatchment | null>(null);
  const [protZones, setProtZones] = useState<RiskProtectionZone[]>([]);
  const [gaps, setGaps] = useState<RiskGap[]>([]);
  const [overlay, setOverlay] = useState<RiskPressureOverlay[]>([]);
  const [checklist, setChecklist] = useState<RiskChecklistItem[]>([]);
  const [evidence, setEvidence] = useState<RiskEvidenceFact[]>([]);
  const [sourceStatus, setSourceStatus] = useState<RiskSourceStatus[]>([]);
  const [genStages, setGenStages] = useState<RiskGenerationStage[]>([]);
  const [generating, setGenerating] = useState(false);
  // Studnie edytuje się w „Danych wejściowych" (uwaga 8), ale punkty rysuje mapa w „Przeglądzie" —
  // stan mieszka więc na stronie, nie w zakładce, i ładuje się niezależnie od tego, gdzie jest użytkownik.
  const [wells, setWells] = useState<RiskAssessmentWell[]>([]);
  // Liczba dokumentów źródłowych — potrzebna do wyboru zakładki startowej i do stanu „czeka na dane"
  // w Przeglądzie. Zakładka dokumentów trzyma własną, świeższą listę (upload nie przechodzi przez run()).
  const [docsCount, setDocsCount] = useState<number | null>(null);
  // Token kolejności: odrzuca odpowiedzi w locie po zmianie oceny / kolejnej mutacji (#26/#34).
  const loadTok = useRef(0);
  // Odświeżenie „fundamentu"; przy błędzie ZACHOWUJE poprzedni stan (nie chowa karty), a przy nieaktualnym
  // tokenie ignoruje odpowiedź. Zwraca Promise, by run() poczekał przed zwolnieniem busy.
  const refreshFoundation = (tok: number) => Promise.all([
    api.catchment(raId).then(v => { if (tok === loadTok.current) setCatchment(v); }).catch(() => {}),
    api.protectionZones(raId).then(v => { if (tok === loadTok.current) setProtZones(v); }).catch(() => {}),
    api.gaps(raId).then(v => { if (tok === loadTok.current) setGaps(v); }).catch(() => {}),
    api.overlay(raId).then(v => { if (tok === loadTok.current) setOverlay(v); }).catch(() => {}),
    api.checklist(raId).then(v => { if (tok === loadTok.current) setChecklist(v); }).catch(() => {}),
    api.evidence(raId).then(v => { if (tok === loadTok.current) setEvidence(v); }).catch(() => {}),
    api.sourceStatus(raId).then(v => { if (tok === loadTok.current) setSourceStatus(v); }).catch(() => {}),
    api.stages(raId).then(v => { if (tok === loadTok.current) setGenStages(v); }).catch(() => {}),
    api.wells(raId).then(v => { if (tok === loadTok.current) setWells(v); }).catch(() => {}),
    api.sourceDocuments(raId).then(v => { if (tok === loadTok.current) setDocsCount(v.length); })
      .catch(() => { if (tok === loadTok.current) setDocsCount(0); }),
  ]);
  const reload = () => {
    const tok = ++loadTok.current;
    api.get(raId).then(v => { if (tok === loadTok.current) setRa(v); })
      .catch(e => { if (tok === loadTok.current) setErr(msg(e)); });
    return refreshFoundation(tok);
  };
  // Wybór zakładki startowej dopiero, gdy znamy OBA składniki decyzji (ocena + liczba dokumentów),
  // inaczej wskoczyłaby „Dane wejściowe" zanim dojedzie lista dokumentów źródłowych.
  useEffect(() => {
    if (tabParam || autoTab || !ra || docsCount == null) return;
    setAutoTab(firstIncompleteTab(ra, docsCount));
  }, [tabParam, autoTab, ra, docsCount]);
  useEffect(() => {
    // czyść stan poprzedniej oceny zanim wczytamy nową (nie pokazuj cudzych danych pod nowym URL-em)
    setRa(null); setCatchment(null); setProtZones([]); setGaps([]); setOverlay([]); setChecklist([]); setEvidence([]); setSourceStatus([]); setGenStages([]); setWells([]); setDocsCount(null); setAutoTab(null); setErr(null);
    reload();
    // meta niesie pasma/skalę macierzy — cicha porażka zostawiłaby macierz na fallbacku sprzecznym
    // z pigułkami z serwera, więc jedna ponowna próba po chwili
    api.meta().then(setMeta).catch(() => {
      setTimeout(() => { api.meta().then(setMeta).catch(() => {}); }, 2000);
    }); /* eslint-disable-next-line */
  }, [raId]);

  /** Uruchamia mutację; podmienia stan i odświeża fundament. onSuccess wywoływane TYLKO po sukcesie
   *  (np. wyczyszczenie/zamknięcie formularza dodawania — #36, aby wpisana treść nie ginęła przy błędzie). */
  const run = (p: Promise<RiskAssessment>, onSuccess?: () => void) => {
    setBusy(true); setErr(null);
    const tok = ++loadTok.current;
    p.then(r => { if (tok === loadTok.current) { setRa(r); if (onSuccess) onSuccess(); } return refreshFoundation(tok); })
      .catch(e => { if (tok === loadTok.current) setErr(msg(e)); })
      .finally(() => setBusy(false));
  };

  /** Auto-generacja ze strumieniem kroków — postęp na żywo (SSE), potem odświeżenie fundamentu.
   *  {@code fromStage}: ponowny przebieg tylko od zadanego kroku (proces 5-krokowy) — wcześniejsze
   *  kroki, ich statusy i pobrane dane zostają, więc NIE czyścimy wtedy listy kroków. */
  const streamGenerate = (fromStage?: string) => {
    setGenerating(true); setBusy(true); setErr(null);
    // BEZ optymistycznego czyszczenia listy kroków: backend i tak emituje PENDING dla uruchamianych
    // kroków, a przy odrzuconym starcie (stan/uprawnienia/sieć) poprzednia historia ma zostać widoczna.
    const tok = ++loadTok.current;
    api.autoGenerateStream(raId, s => {
      if (tok !== loadTok.current) return;
      setGenStages(prev => {
        const i = prev.findIndex(x => x.stageKey === s.stageKey);
        const next = i === -1 ? [...prev, s] : prev.map((x, j) => (j === i ? s : x));
        return next.sort((a, b) => a.sortOrder - b.sortOrder);
      });
    }, fromStage)
      .then(r => { if (tok === loadTok.current) setRa(r); return refreshFoundation(tok); })
      .catch(e => {
        if (tok === loadTok.current) setErr(msg(e));
        // zsynchronizuj kroki ze stanem serwera (np. bieg przerwany w połowie / odrzucony start)
        api.stages(raId).then(v => { if (tok === loadTok.current) setGenStages(v); }).catch(() => {});
      })
      // BEZ bramki tokenem: to jedyne miejsce zdejmujące busy/generating, a nowy przebieg nie może
      // wystartować, póki busy trwa — bramka potrafiła trwale zamrozić stronę po „Odśwież" w trakcie
      .finally(() => { setGenerating(false); setBusy(false); });
  };

  /** F2c: zapis realnego obszaru oddziaływania (OSW) / powrót do liczonego okręgu; odświeża strefę + presje. */
  const saveCatchmentGeom = (geoJson: string, strategy: 'IMPORTED' | 'DRAWN' = 'IMPORTED') => {
    setBusy(true); setErr(null);
    const tok = ++loadTok.current;
    api.saveCatchment(raId, geoJson, strategy)
      .then(() => refreshFoundation(tok))
      .catch(e => { if (tok === loadTok.current) setErr(msg(e)); })
      .finally(() => setBusy(false));
  };
  /** F4: import/usunięcie granicy strefy ochronnej ujęcia (GeoJSON WGS84). */
  const saveProtZone = (geoJson: string, kind: string) => {
    setBusy(true); setErr(null);
    const tok = ++loadTok.current;
    api.saveProtectionZone(raId, geoJson, kind)
      .then(list => { if (tok === loadTok.current) setProtZones(list); })
      .catch(e => { if (tok === loadTok.current) setErr(msg(e)); })
      .finally(() => setBusy(false));
  };
  const deleteProtZones = () => {
    setBusy(true); setErr(null);
    const tok = ++loadTok.current;
    api.deleteProtectionZone(raId)
      .then(list => { if (tok === loadTok.current) setProtZones(list); })
      .catch(e => { if (tok === loadTok.current) setErr(msg(e)); })
      .finally(() => setBusy(false));
  };
  const resetCatchmentGeom = () => {
    setBusy(true); setErr(null);
    const tok = ++loadTok.current;
    api.resetCatchment(raId)
      .then(() => refreshFoundation(tok))
      .catch(e => { if (tok === loadTok.current) setErr(msg(e)); })
      .finally(() => setBusy(false));
  };

  /** Ocena bez ujęcia: ręczny zapis danych ujęcia / AI-odczyt z dokumentacji ujęcia. */
  const saveSpatialData = (data: Record<string, unknown>) => {
    setBusy(true); setErr(null);
    const tok = ++loadTok.current;
    api.saveSpatial(raId, data)
      .then(r => { if (tok === loadTok.current) setRa(r); return refreshFoundation(tok); })
      .catch(e => { if (tok === loadTok.current) setErr(msg(e)); })
      .finally(() => setBusy(false));
  };
  const extractIntakeDoc = (file: File) => {
    setBusy(true); setErr(null);
    const tok = ++loadTok.current;
    api.extractIntakeDocument(raId, file)
      .then(r => { if (tok === loadTok.current) setRa(r); return refreshFoundation(tok); })
      .catch(e => { if (tok === loadTok.current) setErr(msg(e)); })
      .finally(() => setBusy(false));
  };

  if (err && !ra) return <div className="text-red-500">Błąd: {err}</div>;
  if (!ra) return <div className="text-ink-400">Ładowanie…</div>;

  const locked = ra.locked;
  const controlsCount = ra.hazards.reduce((n, h) => n + h.controls.length, 0);

  // Kolejność zakładek odzwierciedla przebieg opracowania (uwagi 14 i 15): najpierw dokumenty,
  // z których AI czyta dane, potem dane wejściowe uzupełniane przez eksperta, dopiero potem
  // przegląd tego, co system ustalił, a na końcu dokument.
  const tabs: [Tab, string, number | null][] = [
    ['sourceDocs', 'Dokumenty źródłowe', docsCount],
    ['input', 'Dane wejściowe', null],
    ['overview', 'Przegląd', null],
    ['hazards', 'Zagrożenia i ryzyko', ra.hazards.length],
    ['monitoring', 'Monitoring', ra.monitoring.length],
    ['document', 'Dokument', ra.documents.length || null],
  ];

  return (
    <div className="max-w-[1200px]">
      <button onClick={() => navigate('/ocena-ryzyka')} className="btn btn-ghost btn-sm mb-3 text-ink-500">
        <Icon name="arrow" className="w-3.5 h-3.5 rotate-180" /> Oceny ryzyka
      </button>

      <header className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-[13px] text-ink-500">{ra.number}</span>
            <span className={'pill pill-' + ra.layerColor}>{ra.layerName} · {ra.legalBasis}</span>
            <span className={'pill pill-' + ra.statusColor}>{ra.statusName}</span>
            {ra.criticalFlag && <span className="pill pill-red"><span className="pill-dot" />ryzyko krytyczne</span>}
          </div>
          <h1 className="mb-0.5">{ra.title}</h1>
          <div className="text-ink-500 text-[13px]">
            {(ra.intakeName || ra.objectName) ?? '—'}{ra.clientName ? ` · ${ra.clientName}` : ''}{ra.town ? ` · ${ra.town}` : ''}
          </div>
        </div>
        <button onClick={() => navigate('/ocena-ryzyka/przewodnik')} className="btn btn-ghost btn-sm text-ink-500"
          title="Jak poprawnie przejść ocenę — krok po kroku">
          <Icon name="shield" className="w-3.5 h-3.5" /> Przewodnik
        </button>
        <a href={api.pdfUrl(ra.id)} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">
          <Icon name="file-text" className="w-3.5 h-3.5" /> Podgląd PDF
        </a>
      </header>

      <Stepper current={stepIndex(ra.status)} cancelled={ra.status === 'ANULOWANY'} />

      {err && <div className="text-red-500 my-2 text-[12px]">Błąd: {err}</div>}

      <div className="flex gap-1 my-3 border-b border-ink-100 flex-wrap">
        {tabs.map(([t, label, count]) => (
          <button key={t} onClick={() => setTab(t)}
            className={'px-3 py-2 text-[13px] border-b-2 -mb-px ' +
              (tab === t ? 'border-water-500 text-ink-900 font-semibold' : 'border-transparent text-ink-500 hover:text-ink-700')}>
            {label}{count != null ? ` (${count})` : ''}
          </button>
        ))}
      </div>

      {tab === 'overview' && <Overview ra={ra} controlsCount={controlsCount} busy={busy} run={run} reload={reload} setErr={setErr}
        canEdit={canEdit} canSign={canSign} canAssign={canAssign} canAI={canAI} canUpload={canUpload} meta={meta} catchment={catchment} protZones={protZones} gaps={gaps} overlay={overlay} checklist={checklist} evidence={evidence} sourceStatus={sourceStatus} onSourceStatus={setSourceStatus}
        genStages={genStages} generating={generating} streamGenerate={streamGenerate}
        saveCatchmentGeom={saveCatchmentGeom} resetCatchmentGeom={resetCatchmentGeom}
        saveProtZone={saveProtZone} deleteProtZones={deleteProtZones} wells={wells}
        docsCount={docsCount} onGoTab={setTab}
        saveSpatialData={saveSpatialData} extractIntakeDoc={extractIntakeDoc} />}
      {tab === 'input' && <InputTab ra={ra} meta={meta} busy={busy} run={run} canEdit={canEdit && !locked} canAI={canAI}
        setErr={setErr} onWellsChanged={setWells} saveSpatialData={saveSpatialData} extractIntakeDoc={extractIntakeDoc} />}
      {tab === 'sourceDocs' && <SourceDocumentsTab raId={ra.id} canEdit={canEdit && !locked} canAI={canAI} setErr={setErr} onDocsCount={setDocsCount} />}
      {tab === 'hazards' && <HazardsTab ra={ra} meta={meta} busy={busy} run={run} canEdit={canEdit && !locked} canAI={canAI} />}
      {tab === 'monitoring' && <MonitoringTab ra={ra} meta={meta} busy={busy} run={run} canEdit={canEdit && !locked} />}
      {tab === 'document' && <DocumentTab ra={ra} busy={busy} run={run} reload={reload} setRa={setRa} setErr={setErr}
        canEdit={canEdit && !locked} canAI={canAI} canSign={canSign} />}
    </div>
  );
}

/**
 * Scala WSZYSTKIE poligony z wgranego GeoJSON (geometria / Feature / FeatureCollection) w jedną
 * geometrię. Eksporty QGIS/geoportalu to zwykle FeatureCollection z osobnym Feature na każdą część —
 * branie tylko features[0] cicho ucinałoby wieloczęściowe strefy/obszary.
 */
function extractPolygons(raw: unknown): GeoJSON.Geometry | null {
  const polys: GeoJSON.Position[][][] = [];
  const collect = (g: unknown) => {
    const geom = g as { type?: string; coordinates?: unknown } | null;
    if (!geom?.type) return;
    if (geom.type === 'Polygon') polys.push(geom.coordinates as GeoJSON.Position[][]);
    else if (geom.type === 'MultiPolygon') polys.push(...(geom.coordinates as GeoJSON.Position[][][]));
  };
  const r = raw as { type?: string; features?: unknown[]; geometry?: unknown } | null;
  if (r?.type === 'FeatureCollection') (r.features ?? []).forEach(f => collect((f as { geometry?: unknown })?.geometry));
  else if (r?.type === 'Feature') collect(r.geometry);
  else collect(r);
  if (polys.length === 0) return null;
  return polys.length === 1
    ? { type: 'Polygon', coordinates: polys[0] }
    : { type: 'MultiPolygon', coordinates: polys };
}

/** Wspólne wczytanie pliku GeoJSON (limit 10 MB, obsługa błędu odczytu) → geometria poligonowa. */
function readPolygonFile(f: File, onGeom: (geomJson: string) => void, onError: (m: string) => void) {
  if (f.size > 10 * 1024 * 1024) { onError('Plik GeoJSON przekracza limit 10 MB.'); return; }
  const rd = new FileReader();
  rd.onerror = () => onError('Nie udało się odczytać pliku.');
  rd.onload = () => {
    try {
      const geom = extractPolygons(JSON.parse(String(rd.result)));
      if (!geom) { onError('Plik musi zawierać poligon (Polygon/MultiPolygon) w układzie WGS84.'); return; }
      onGeom(JSON.stringify(geom));
    } catch { onError('Niepoprawny plik GeoJSON.'); }
  };
  rd.readAsText(f);
}

// ==================== Stepper ====================
function Stepper({ current, cancelled }: { current: number; cancelled: boolean }) {
  return (
    <div className="card"><div className="card-body py-3">
      {cancelled && <div className="text-[12px] text-red-500 font-semibold mb-2">Zlecenie anulowane</div>}
      <div className="flex items-center gap-1">
        {STEPS.map((label, i) => (
          <div key={label} className="flex-1 flex items-center gap-1 min-w-0">
            <div className="flex flex-col items-center gap-1 flex-shrink-0">
              <div className={'w-6 h-6 rounded-full grid place-items-center text-[11px] font-semibold ' +
                (i < current ? 'bg-green-500 text-white' : i === current ? 'bg-water-500 text-white' : 'bg-ink-100 text-ink-400')}>
                {i < current ? <Icon name="check" className="w-3 h-3" /> : i + 1}
              </div>
              <span className={'text-[10px] whitespace-nowrap ' + (i === current ? 'text-ink-800 font-semibold' : 'text-ink-400')}>{label}</span>
            </div>
            {i < STEPS.length - 1 && <div className={'flex-1 h-0.5 rounded ' + (i < current ? 'bg-green-500' : 'bg-ink-100')} />}
          </div>
        ))}
      </div>
    </div></div>
  );
}

// ==================== Proces 5-krokowy generacji ====================
const GEN_STAGE_META: Record<string, { label: string; cls: string; icon: string; spin?: boolean }> = {
  PENDING: { label: 'oczekuje', cls: 'text-ink-400', icon: '○' },
  RUNNING: { label: 'w toku', cls: 'text-water-600', icon: '◐', spin: true },
  OK: { label: 'gotowe', cls: 'text-green-600', icon: '✓' },
  FAILED: { label: 'błąd', cls: 'text-red-600', icon: '✕' },
};

/** Stała definicja 5 kroków (układ raportu z art. 4f ust. 2 / TrinkwEGV) — kolejność + podpowiedź,
 *  co dany krok robi i skąd bierze dane, widoczna zanim krok pierwszy raz się uruchomi. */
const GEN_STEPS: { key: string; label: string; hint: string }[] = [
  { key: 'OBSZAR', label: 'Obszar zasilania i opis', hint: 'dane wejściowe + geometria obszaru + odczyty państwowe (JCWPd, GZWP, powódź, obszary chronione)' },
  { key: 'ZAGROZENIA', label: 'Inwentarz zagrożeń i zdarzeń', hint: 'biblioteka wzorców + presje wykryte w obszarze (zakłady SEVESO i E-PRTR, BDOT10k, MIDAS, CLC, Natura 2000)' },
  { key: 'RYZYKO', label: 'Szacowanie ryzyka', hint: 'macierz P×D — priorytety ryzyka wyjściowego' },
  { key: 'SRODKI', label: 'Środki zarządzania ryzykiem', hint: 'propozycje środków z katalogu + ryzyko rezydualne' },
  { key: 'PROGRAM_BADAN', label: 'Program badań', hint: 'program monitoringu + punkty monitoringu PSH + rejestr luk' },
];

/** Proces 5-krokowy: status kroków na żywo (SSE) + per krok „co pobrano automatycznie" (fakty ledgera
 *  tagowane krokiem, zbiorczy stan źródeł) + ponowny przebieg od wybranego kroku. „Nic w tle". */
function GenerationSteps({ stages, generating, evidence, sourceStatus, canRun, busy, onRunFrom }: {
  stages: RiskGenerationStage[]; generating: boolean; evidence: RiskEvidenceFact[];
  sourceStatus: RiskSourceStatus[]; canRun: boolean; busy: boolean; onRunFrom: (fromStage?: string) => void;
}) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const byKey = useMemo(() => new Map(stages.map(s => [s.stageKey, s])), [stages]);
  const hasRun = stages.length > 0;
  // re-run częściowy ma sens tylko, gdy K1 realnie się ukończył (backendowy guard i tak to wymusza)
  const k1Ok = byKey.get('OBSZAR')?.status === 'OK';
  const failed = stages.some(s => s.status === 'FAILED');
  return (
    <div className={'card border-l-4 ' + (failed ? 'border-l-red-400' : 'border-l-water-400')}>
      <div className="card-head flex items-center gap-2 flex-wrap">
        <span className="card-title">Proces generacji — 5 kroków</span>
        <span className="text-[11px] text-ink-400">układ raportu z art. 4f ust. 2 · każdy krok pokazuje pobrane dane</span>
        {generating
          ? <span className="ml-auto text-[11px] text-water-600">generowanie w toku…</span>
          : failed
            ? <span className="ml-auto text-[11px] text-red-600">zakończono z błędem kroku</span>
            : hasRun
              ? <span className="ml-auto text-[11px] text-green-600">zakończono</span>
              : <span className="ml-auto text-[11px] text-ink-400">jeszcze nie uruchomiono</span>}
      </div>
      <div className="card-body grid gap-1.5 text-[13px]">
        {GEN_STEPS.map((step, i) => {
          const s = byKey.get(step.key);
          const m = GEN_STAGE_META[s?.status ?? 'PENDING'] ?? GEN_STAGE_META.PENDING;
          const facts = evidence.filter(f => f.stage === step.key);
          const sources = sourceStatus.filter(src => src.stage === step.key);
          const srcOk = sources.filter(x => x.status === 'OK').length;
          const srcErr = sources.filter(x => x.status === 'BLAD').length;
          const srcNa = sources.filter(x => x.status === 'NIEDOSTEPNE' || x.status === 'NIE_POBRANO').length;
          const expanded = open[step.key] ?? s?.status === 'FAILED';
          const toggleable = facts.length > 0 || sources.length > 0;
          return (
            <div key={step.key}
              className={'rounded border border-ink-100 px-3 py-2' + (s?.status === 'RUNNING' ? ' bg-water-500/5' : '')}>
              <div className="flex items-start gap-2">
                <span className={'font-mono w-4 text-center shrink-0 mt-0.5 ' + m.cls + (m.spin ? ' inline-block animate-spin' : '')}>{m.icon}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-semibold text-ink-400">Krok {i + 1}</span>
                    <span className="text-ink-800 font-medium">{s?.label ?? step.label}</span>
                    <span className={'text-[10px] ' + m.cls}>{m.label}</span>
                  </div>
                  <div className="text-[11px] text-ink-400">{s?.message ?? step.hint}</div>
                </div>
                <span className="flex items-center gap-2 shrink-0">
                  {canRun && hasRun && !generating && (i === 0 || k1Ok) && (
                    <button type="button" disabled={busy}
                      className="text-[11px] text-water-600 hover:underline disabled:opacity-50 disabled:no-underline whitespace-nowrap"
                      onClick={() => {
                        const q = i === 0
                          ? 'Uruchomić ponownie wszystkie kroki? Auto-uzupełni puste dane i dosieje szkic (nie nadpisze Twoich zmian).'
                          : `Uruchomić ponownie kroki ${i + 1}–5? Wcześniejsze kroki i ich pobrane dane zostaną bez zmian.`;
                        if (window.confirm(q)) onRunFrom(i === 0 ? undefined : step.key);
                      }}>
                      {i === 0 ? 'Uruchom wszystko' : 'Od tego kroku'}
                    </button>
                  )}
                  {(toggleable || expanded) && (
                    <button type="button" className="text-[11px] text-ink-500 hover:text-ink-800 whitespace-nowrap"
                      onClick={() => setOpen(o => ({ ...o, [step.key]: !expanded }))}>
                      {expanded ? 'Zwiń' : `Pobrane dane (${facts.length})`}
                    </button>
                  )}
                </span>
              </div>
              {expanded && (
                <div className="mt-2 ml-6 grid gap-1.5">
                  {sources.length > 0 && (
                    <div className="text-[11px] text-ink-400">
                      Źródła kroku: <span className="text-green-600">{srcOk} odczytane</span>
                      {srcErr > 0 && <> · <span className="text-red-600">{srcErr} błędy</span></>}
                      {srcNa > 0 && <> · <span>{srcNa} niedostępne / nie pobrano</span></>}
                      <span> — szczegóły i akcje w panelu „Status źródeł danych"</span>
                    </div>
                  )}
                  {facts.length > 0 ? facts.map(f => (
                    <div key={f.id} className="flex items-start gap-2">
                      <span className="text-ink-700 min-w-0">
                        {f.label ?? f.factKey}:
                        <span className="text-ink-500"> {f.value ?? '—'}{f.distanceM != null ? ` · ${Math.round(f.distanceM)} m` : ''}</span>
                      </span>
                      <span className="ml-auto flex items-center gap-1.5 shrink-0">
                        {f.sourceDataset && <span className="pill pill-water text-[9px]">{f.sourceDataset}</span>}
                        {f.fetchedAt && <span className="text-[10px] text-ink-400 font-mono">{f.fetchedAt.slice(0, 10)}</span>}
                      </span>
                    </div>
                  )) : (
                    <div className="text-[11px] text-ink-400">
                      {sources.length > 0
                        ? 'Brak zapisanych faktów dla tego kroku — dane nie zostały jeszcze pobrane (patrz status kroku i źródeł).'
                        : 'Ten krok nie pobiera danych zewnętrznych — wynik powstaje z reguł i biblioteki wzorców (patrz komunikat kroku).'}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ==================== Dane ujęcia — ocena bez powiązania (ręcznie / AI z dokumentacji ujęcia) ====================
function SpatialEditCard({ ra, busy, canEdit, onSave, onExtract, setErr }: {
  ra: RiskAssessment; busy: boolean; canEdit: boolean;
  onSave: (d: Record<string, unknown>) => void; onExtract: (f: File) => void; setErr: (m: string | null) => void;
}) {
  const [objectName, setObjectName] = useState(ra.objectName ?? '');
  const [town, setTown] = useState(ra.town ?? '');
  const [lat, setLat] = useState(ra.latitude != null ? String(ra.latitude) : '');
  const [lng, setLng] = useState(ra.longitude != null ? String(ra.longitude) : '');
  const [jcwpd, setJcwpd] = useState(ra.jcwpdCode ?? '');
  useEffect(() => {
    setObjectName(ra.objectName ?? ''); setTown(ra.town ?? '');
    setLat(ra.latitude != null ? String(ra.latitude) : '');
    setLng(ra.longitude != null ? String(ra.longitude) : '');
    setJcwpd(ra.jcwpdCode ?? '');
  }, [ra.id, ra.latitude, ra.longitude, ra.jcwpdCode, ra.objectName, ra.town]);
  const save = () => onSave({
    objectName: objectName || null, town: town || null,
    latitude: lat ? Number(lat) : null, longitude: lng ? Number(lng) : null, jcwpdCode: jcwpd || null,
  });
  return (
    <div className="card">
      <div className="card-head flex items-center gap-2">
        <span className="card-title">Dane ujęcia (ocena bez powiązania)</span>
        <span className="text-[11px] text-ink-400">wpisz ręcznie lub odczytaj AI z dokumentacji ujęcia</span>
      </div>
      <div className="card-body grid gap-2 text-[13px]">
        <div className="grid grid-cols-2 gap-2">
          <label className="field"><span className="field-label">Nazwa obiektu</span>
            <input className="input" value={objectName} disabled={!canEdit} onChange={e => setObjectName(e.target.value)} /></label>
          <label className="field"><span className="field-label">Miejscowość</span>
            <input className="input" value={town} disabled={!canEdit} onChange={e => setTown(e.target.value)} /></label>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <label className="field"><span className="field-label">Szerokość (lat)</span>
            <input className="input" value={lat} disabled={!canEdit} inputMode="decimal" placeholder="np. 54.4600" onChange={e => setLat(e.target.value)} /></label>
          <label className="field"><span className="field-label">Długość (lng)</span>
            <input className="input" value={lng} disabled={!canEdit} inputMode="decimal" placeholder="np. 17.0450" onChange={e => setLng(e.target.value)} /></label>
          <label className="field"><span className="field-label">JCWPd (kod)</span>
            <input className="input" value={jcwpd} disabled={!canEdit} placeholder="np. PLGW600011" onChange={e => setJcwpd(e.target.value)} /></label>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={save} disabled={busy} className="btn btn-primary btn-sm"><Icon name="check" className="w-3.5 h-3.5" /> Zapisz dane ujęcia</button>
            <label className={'btn btn-sm btn-ai ' + (busy ? 'opacity-50 pointer-events-none' : '')}>
              <Icon name="sparkles" className="w-3.5 h-3.5" /> Wczytaj dokumentację ujęcia
              <input type="file" accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.tif,.tiff" className="hidden" disabled={busy}
                onChange={e => {
                  const f = e.target.files?.[0]; (e.target as HTMLInputElement).value = '';
                  if (!f) return;
                  const err = validateUploadSize(f);
                  if (err) { setErr(err); return; }
                  onExtract(f);
                }} />
            </label>
            <span className="text-[11px] text-ink-400">AI wyciągnie współrzędne, JCWPd i nazwę z operatu / dokumentacji ujęcia.</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== Overview ====================
const CATCHMENT_STRATEGY: Record<string, string> = {
  SICHARDT_BUFFER: 'Bufor Sichardta', DOC: 'Dokumentacja hydrogeologiczna', MPHP: 'Zlewnia MPHP10k', WYSSLING: 'Strefa Wysslinga',
};

const SOURCE_STATUS_META: Record<string, { label: string; cls: string }> = {
  OK: { label: 'odczytano', cls: 'pill-green' },
  BLAD: { label: 'błąd', cls: 'pill-red' },
  PUSTE: { label: 'brak obiektów', cls: 'pill-amber' },
  NIE_POBRANO: { label: 'nie pobrano', cls: 'pill-gray' },
  NIEDOSTEPNE: { label: 'niedostępne', cls: 'pill-gray' },
};
const SOURCE_CATEGORY_LABEL: Record<string, string> = {
  PUNKTOWE: 'Odczyty punktowe (dla ujęcia)',
  PRESJE: 'Warstwy presji (SEVESO · E-PRTR · BDOT10k · MIDAS · CLC)',
  KARTY: 'Karty charakterystyki JCWPd (aPGW) — kontekst regionalny jednolitej części wód',
  MONITORING: 'Monitoring wód podziemnych (PIG-PIB PSH) — punkty i wyniki badań w rejonie ujęcia',
  NIEDOSTEPNE: 'Źródła niedostępne maszynowo — wymagają ręcznego wgrania',
};

const REREADABLE_SOURCES = new Set(['JCWPD', 'FLOOD', 'PROTECTED_AREAS']);

/** Panel F1: skonsolidowany status źródeł — co odczytano automatycznie, co z API, czego nie odczytano.
 *  Odczyty punktowe mają „Odczytaj ponownie" (wymuszony świeży lookup dla ujęcia); warstwy presji
 *  pobieralne kopertowo mają „Pobierz dla tej oceny" (ingest wokół współrzędnych oceny + przeliczenie
 *  overlay). Wszystko w module oceny — bez wyprowadzania na „Mapy · GIS". */
function SourceStatusPanel({ items, raId, canEdit, canUpload, hasIntake, hasCoords, onUpdated, onError, onFetched }: {
  items: RiskSourceStatus[]; raId: number; canEdit: boolean; canUpload: boolean; hasIntake: boolean; hasCoords: boolean;
  onUpdated: (list: RiskSourceStatus[]) => void; onError: (m: string | null) => void; onFetched: () => void;
}) {
  const [loadingCode, setLoadingCode] = useState<string | null>(null);
  if (items.length === 0) return null;
  const ok = items.filter(i => i.status === 'OK').length;
  const err = items.filter(i => i.status === 'BLAD').length;
  const na = items.filter(i => i.status === 'NIEDOSTEPNE').length;
  const cats = ['PUNKTOWE', 'PRESJE', 'KARTY', 'MONITORING', 'NIEDOSTEPNE'];
  const canReread = canEdit && hasIntake;
  const anyRereadable = items.some(i => i.category === 'PUNKTOWE' && REREADABLE_SOURCES.has(i.code));

  const doReread = (p: Promise<RiskSourceStatus[]>, key: string) => {
    setLoadingCode(key); onError(null);
    p.then(onUpdated).catch(e => onError(msg(e))).finally(() => setLoadingCode(null));
  };
  const doFetch = (code: string) => {
    setLoadingCode(code); onError(null);
    api.fetchSourceLayer(raId, code)
      .then(list => { onUpdated(list); onFetched(); })
      .catch(e => onError(msg(e)))
      .finally(() => setLoadingCode(null));
  };

  return (
    <div className="card">
      <div className="card-head flex items-center flex-wrap gap-2">
        <span className="card-title">Status źródeł danych</span>
        <span className="text-[11px] text-ink-400">co odczytano · co z podpiętych API · czego nie udało się odczytać</span>
        <span className="ml-auto flex items-center gap-1.5">
          <span className="pill pill-green text-[10px]">{ok} odczytane</span>
          {err > 0 && <span className="pill pill-red text-[10px]">{err} błędy</span>}
          {na > 0 && <span className="pill pill-gray text-[10px]">{na} niedostępne</span>}
          {canReread && anyRereadable && (
            <button type="button" className="btn btn-sm btn-secondary" disabled={loadingCode != null}
              onClick={() => doReread(api.rereadAllSources(raId), '*')}>
              {loadingCode === '*' ? 'Odczytuję…' : 'Odczytaj wszystkie'}
            </button>
          )}
        </span>
      </div>
      <div className="card-body grid gap-3 text-[13px]">
        {cats.map(cat => {
          const rows = items.filter(i => i.category === cat);
          if (rows.length === 0) return null;
          return (
            <div key={cat} className="grid gap-1">
              <div className="text-[11px] uppercase tracking-wide text-ink-400">{SOURCE_CATEGORY_LABEL[cat] ?? cat}</div>
              {rows.map(r => {
                const m = SOURCE_STATUS_META[r.status] ?? { label: r.status, cls: 'pill-gray' };
                const rereadable = canReread && r.category === 'PUNKTOWE' && REREADABLE_SOURCES.has(r.code);
                const loading = loadingCode === r.code || loadingCode === '*';
                return (
                  <div key={r.category + ':' + r.code} className="flex items-start gap-2">
                    <span className={'pill text-[9px] mt-0.5 shrink-0 ' + m.cls}>{m.label}</span>
                    <div className="min-w-0">
                      <div className="text-ink-800">{r.name}</div>
                      {r.detail && <div className="text-[11px] text-ink-400">{r.detail}</div>}
                    </div>
                    <span className="ml-auto flex items-center gap-2 shrink-0">
                      {r.fetchedAt && <span className="text-[10px] text-ink-400 font-mono whitespace-nowrap">{r.fetchedAt.slice(0, 10)}</span>}
                      {rereadable && (
                        <button type="button"
                          className="text-[11px] text-water-600 hover:underline disabled:opacity-50 disabled:no-underline whitespace-nowrap"
                          disabled={loadingCode != null}
                          onClick={() => doReread(api.rereadSource(raId, r.code), r.code)}>
                          {loading ? 'Odczytuję…' : (r.status === 'NIE_POBRANO' ? 'Odpytaj' : 'Odczytaj ponownie')}
                        </button>
                      )}
                      {/* Bez warunku na status: dane u źródła się zmieniają (zakłady przybywają i są wycofywane),
                          a warstwa jest globalna — po pierwszym pobraniu ekran oceny musi pozwalać ją odświeżyć,
                          inaczej jedyną drogą zostaje cron niedzielny albo panel „Mapy · GIS". */}
                      {/* Karty JCWPd nie wymagają współrzędnych — kluczem jest kod jednolitej części wód,
                          więc pobranie działa także dla oceny bez ujęcia i bez punktu na mapie. */}
                      {(r.category === 'PRESJE' || r.category === 'KARTY') && r.fetchScope && r.fetchScope !== 'UPLOAD'
                        && canEdit && (hasCoords || r.category === 'KARTY') && (
                        <button type="button"
                          className="text-[11px] text-water-600 hover:underline disabled:opacity-50 disabled:no-underline whitespace-nowrap"
                          disabled={loadingCode != null}
                          onClick={() => {
                            // Karty JCWPd to jedno żądanie o komplet 174 rekordów — bez ostrzeżenia o czasie.
                            if (r.fetchScope === 'GLOBAL' && r.category !== 'KARTY' && !window.confirm(
                              'To pełny import krajowy tej warstwy (cała Polska) — może potrwać kilkadziesiąt sekund. Kontynuować?')) return;
                            doFetch(r.code);
                          }}>
                          {loadingCode === r.code ? 'Pobieram…'
                            : (r.status === 'OK' ? 'Odśwież' : 'Pobierz')
                              + (r.category === 'KARTY' ? ' komplet kart'
                                : r.fetchScope === 'GLOBAL' ? ' (import krajowy)' : ' dla tej oceny')}
                        </button>
                      )}
                      {/* Sterowane osobną flagą, NIE fetchScope: SEVESO i E-PRTR mają konektor maszynowy
                          (GLOBAL), ale upload zostaje ścieżką awaryjną na wypadek niedostępności GIOŚ —
                          związanie kontrolki z fetchScope==='UPLOAD' uczyniłoby ją nieosiągalną. */}
                      {r.category === 'PRESJE' && r.uploadable && canUpload && (
                        <label className={'text-[11px] hover:underline whitespace-nowrap cursor-pointer '
                          + (r.fetchScope === 'UPLOAD' ? 'text-water-600 ' : 'text-ink-400 ')
                          + (loadingCode != null ? 'opacity-50 pointer-events-none' : '')}
                          title={r.fetchScope === 'UPLOAD'
                            ? 'Import podmienia CAŁĄ globalną warstwę (wymaga uprawnień administracyjnych)'
                            : 'Ścieżka awaryjna — ta warstwa ma konektor maszynowy. Import podmieni CAŁĄ globalną '
                              + 'warstwę danymi z pliku i oznaczy ją jako import ręczny.'}>
                          {loadingCode === r.code ? 'Importuję…'
                            : r.fetchScope !== 'UPLOAD' ? 'Wgraj plik (awaryjnie)'
                            : (r.status === 'OK' ? 'Wgraj ponownie (GeoJSON)' : 'Wgraj plik (GeoJSON)')}
                          <input type="file" accept=".geojson,.json,application/geo+json" className="hidden"
                            disabled={loadingCode != null}
                            onChange={e => {
                              const f = e.target.files?.[0]; (e.target as HTMLInputElement).value = '';
                              if (!f) return;
                              // Lustro limitu serwerowego (RiskAssessmentApi): paczki z ETL geobazy aPGW
                              // (kody PZ_*) mają wyższy próg, bo ich rozmiar jest oczekiwany — sama warstwa
                              // poborów wód podziemnych to ~18 MB.
                              const limitMb = r.code.startsWith('PZ_') ? 40 : 10;
                              if (f.size > limitMb * 1024 * 1024) { onError(`Plik GeoJSON przekracza limit ${limitMb} MB.`); return; }
                              if (!window.confirm(`Import podmieni CAŁĄ zawartość warstwy ${r.code} (globalnie, dla wszystkich ocen). Kontynuować?`)) return;
                              setLoadingCode(r.code); onError(null);
                              api.uploadSourceLayer(raId, r.code, f)
                                .then(list => { onUpdated(list); onFetched(); })
                                .catch(err => onError(msg(err)))
                                .finally(() => setLoadingCode(null));
                            }} />
                        </label>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Overview({ ra, controlsCount, busy, run, reload, setErr, canEdit, canSign, canAssign, canAI, canUpload, meta, catchment, protZones, gaps, overlay, checklist, evidence, sourceStatus, onSourceStatus, genStages, generating, streamGenerate, saveCatchmentGeom, resetCatchmentGeom, saveProtZone, deleteProtZones, wells, docsCount, onGoTab, saveSpatialData, extractIntakeDoc }: {
  ra: RiskAssessment; controlsCount: number; busy: boolean; run: (p: Promise<RiskAssessment>, onSuccess?: () => void) => void;
  reload: () => void; setErr: (m: string | null) => void; canEdit: boolean; canSign: boolean; canAssign: boolean; canAI: boolean; canUpload: boolean; meta: RiskMeta | null;
  catchment: RiskCatchment | null; protZones: RiskProtectionZone[]; gaps: RiskGap[]; overlay: RiskPressureOverlay[]; checklist: RiskChecklistItem[]; evidence: RiskEvidenceFact[]; sourceStatus: RiskSourceStatus[]; onSourceStatus: (list: RiskSourceStatus[]) => void;
  genStages: RiskGenerationStage[]; generating: boolean; streamGenerate: (fromStage?: string) => void;
  saveCatchmentGeom: (geoJson: string, strategy?: 'IMPORTED' | 'DRAWN') => void; resetCatchmentGeom: () => void;
  saveProtZone: (geoJson: string, kind: string) => void; deleteProtZones: () => void;
  wells: RiskAssessmentWell[]; docsCount: number | null; onGoTab: (t: Tab) => void;
  saveSpatialData: (d: Record<string, unknown>) => void; extractIntakeDoc: (f: File) => void;
}) {
  const s = ra.status;
  // Postęp wczytywania dokumentu (null = brak wysyłki); reset po zakończeniu run() (busy → false)
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  // F4: rodzaj strefy ochronnej dla importu granicy
  const [pzKind, setPzKind] = useState('POSREDNIA');
  useEffect(() => { if (!busy) setUploadPct(null); }, [busy]);
  // Bumpowane po pobraniu warstwy presji „dla tej oceny" → OcenaMap ponownie czyta overlay presji.
  const [overlayKey, setOverlayKey] = useState(0);
  /** Wybrany obiekt presji (id wiersza overlay) — steruje panelem szczegółów i podświetleniem na mapie. */
  const [selectedPressure, setSelectedPressure] = useState<number | null>(null);
  /** Zmiana/zamknięcie wyboru gasi podświetlenie; nowe zapala panel po wczytaniu szczegółów. */
  const wybierzPresje = (id: number | null) => { setSelectedPressure(id); setMapHighlight(null); };
  /** Rozwinięta grupa presji (klucz warstwa::etykieta) — grupy liczą nawet setki obiektów. */
  const [expandedPressure, setExpandedPressure] = useState<string | null>(null);
  /** Żądanie dolotu mapy do geometrii obiektu; nonce wymusza reakcję także przy tym samym obiekcie. */
  const [mapFocus, setMapFocus] = useState<{ geoJson: string; nonce: number } | null>(null);
  const [showGeoref, setShowGeoref] = useState(false);
  /** Bump po zapisie mapy z georeferencera — odświeża kafelek podglądu proweniencji (i cache-bust obrazu). */
  const [mapPreviewKey, setMapPreviewKey] = useState(0);
  /** Geometria obiektu WYRÓŻNIONEGO na mapie — trzyma się, dopóki obiekt jest wybrany na liście. */
  const [mapHighlight, setMapHighlight] = useState<string | null>(null);
  /** Punkt wskazany na mapie do dodania WŁASNEGO zagrożenia (otwiera formularz); null = brak. */
  const [pendingHazard, setPendingHazard] = useState<{ lng: number; lat: number } | null>(null);
  /** Zagrożenia mające współrzędne — renderowane jako punkty na mapie poglądowej. */
  const hazardPoints = useMemo(() => ra.hazards
    .filter(h => h.latitude != null && h.longitude != null)
    .map(h => ({ id: h.id, title: h.title, categoryColor: h.categoryColor, latitude: h.latitude, longitude: h.longitude })),
    [ra.hazards]);
  /** Zwinięte TYPY presji (klucz = kod warstwy); domyślnie wszystkie rozwinięte. */
  const [collapsedTypes, setCollapsedTypes] = useState<Set<string>>(new Set());
  /** Mapa jest WYŻEJ niż karta presji — po „Na mapie" trzeba do niej przewinąć, inaczej dolot jest niewidoczny. */
  const mapRef = useRef<HTMLDivElement | null>(null);
  const pokazNaMapie = (geoJson: string) => {
    setMapFocus({ geoJson, nonce: Date.now() });
    const el = mapRef.current;
    if (!el) return;
    const przed = window.scrollY;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // płynne przewijanie bywa ignorowane (prefers-reduced-motion, środowiska automatyzacji),
    // a wtedy „Na mapie" nie robiłoby nic widocznego — domykamy skokiem, gdy nic się nie ruszyło
    window.setTimeout(() => {
      if (Math.abs(window.scrollY - przed) < 4) el.scrollIntoView({ block: 'center' });
    }, 300);
  };
  // Grupowanie presji do WYŚWIETLENIA po (warstwa + nazwa): te same obiekty / wieloczęściowe / segmenty o tej
  // samej nazwie (np. drogi BDOT, tereny górnicze) pokazują się jako jeden wiersz z licznikiem. Dane overlay
  // i derywacja zagrożeń pozostają nietknięte (dedup tylko w UI).
  // Grupujemy po CZYTELNEJ etykiecie, ale zachowujemy identyfikatory obiektów (members) — bez nich
  // grupa „×818" nie miałaby czego pokazać w szczegółach ani wskazać na mapie.
  const pressureTypes = useMemo(() => {
    type Member = { overlayId: number; distanceM: number | null; intersects: boolean };
    const groups = new Map<string, {
      sourceLayer: string; layerLabel: string; name: string; count: number; intersects: boolean;
      distanceM: number | null; members: Member[];
    }>();
    for (const o of overlay) {
      const name = o.featureLabel ?? o.featureName ?? o.featureId ?? '—';
      const key = o.sourceLayer + '::' + name;
      const m: Member = { overlayId: o.id, distanceM: o.distanceM, intersects: o.intersects };
      const g = groups.get(key);
      if (!g) groups.set(key, { sourceLayer: o.sourceLayer, layerLabel: o.layerLabel ?? o.sourceLayer,
        name, count: 1, intersects: o.intersects, distanceM: o.distanceM, members: [m] });
      else {
        g.count += 1;
        g.intersects = g.intersects || o.intersects;
        if (o.distanceM != null && (g.distanceM == null || o.distanceM < g.distanceM)) g.distanceM = o.distanceM;
        g.members.push(m);
      }
    }
    // w grupie najbliższe obiekty pierwsze — to one są istotne dla oceny
    for (const g of groups.values()) {
      g.members.sort((a, b) => (a.distanceM ?? Infinity) - (b.distanceM ?? Infinity));
    }
    // DRUGI poziom: zbierz grupy w TYPY (warstwy) — hydrogeolog czyta inwentarz sektorami,
    // a nie płaską listą, w której „Uprawa na gruntach ornych" i „Roślinność trawiasta"
    // stoją osobno mimo wspólnego źródła
    const grupyArr = [...groups.values()];
    type Grupa = (typeof grupyArr)[number];
    const typy = new Map<string, {
      sourceLayer: string; layerLabel: string; grupy: Grupa[];
      obiektow: number; wZlewni: number; distanceM: number | null;
    }>();
    for (const g of grupyArr) {
      const t = typy.get(g.sourceLayer);
      if (!t) {
        typy.set(g.sourceLayer, {
          sourceLayer: g.sourceLayer, layerLabel: g.layerLabel, grupy: [g],
          obiektow: g.count, wZlewni: g.intersects ? g.count : 0, distanceM: g.distanceM,
        });
      } else {
        t.grupy.push(g);
        t.obiektow += g.count;
        if (g.intersects) t.wZlewni += g.count;
        if (g.distanceM != null && (t.distanceM == null || g.distanceM < t.distanceM)) t.distanceM = g.distanceM;
      }
    }
    const out = [...typy.values()];
    // typy z obiektami W zlewni pierwsze, potem wg najbliższego obiektu
    out.sort((a, b) => (b.wZlewni > 0 ? 1 : 0) - (a.wZlewni > 0 ? 1 : 0)
      || (a.distanceM ?? Infinity) - (b.distanceM ?? Infinity));
    for (const t of out) {
      t.grupy.sort((a, b) => (b.intersects ? 1 : 0) - (a.intersects ? 1 : 0)
        || (a.distanceM ?? Infinity) - (b.distanceM ?? Infinity));
    }
    return out;
  }, [overlay]);
  const hasProposed = ra.hazards.some(h => h.status === 'PROPONOWANE');
  const autoStates: RiskAssessmentStatusCode[] = ['NOWE', 'OCZEKUJE_NA_UZUPELNIENIE', 'W_KOLEJCE', 'W_PRZYGOTOWANIU', 'DO_WERYFIKACJI', 'ZWROCONY'];

  /**
   * Uwaga 5: „Generuj sekcje" wołało zwykły POST pod globalnym `busy` — efekt lądował w zakładce
   * „Dokument", więc z Przeglądu wyglądało to na kliknięcie w próżnię. Teraz widać, że operacja trwa,
   * a po jej końcu ile sekcji powstało i jak do nich przejść.
   */
  const [sectionsInfo, setSectionsInfo] = useState<{ count: number } | null>(null);
  const [sectionsBusy, setSectionsBusy] = useState(false);
  const generateSections = () => {
    setSectionsInfo(null); setSectionsBusy(true);
    run(api.generateAllSections(ra.id).then(r => {
      setSectionsInfo({ count: r.sections.filter(x => (x.editedText ?? x.draftText ?? '').trim().length > 0).length });
      return r;
    }).finally(() => setSectionsBusy(false)));
  };

  /** Uwaga 6: akcja zmienia właściciela kroku i wysyła powiadomienie — bez recenzenta nie trafia do nikogo. */
  const submitForReview = () => {
    const kto = ra.reviewerName
      ? `Powiadomienie dostanie recenzent: ${ra.reviewerName}.`
      : 'UWAGA: nie przypisano recenzenta — nikt nie dostanie powiadomienia. Przypisz go w karcie „Zespół" poniżej.';
    if (window.confirm(`Przekazać ocenę do recenzji?\n\nStatus zmieni się na „Do recenzji". ${kto}\n\nOcena pozostaje edytowalna aż do podpisu.`)) {
      run(api.submitReview(ra.id));
    }
  };
  const actions: { label: string; icon: string; cls: string; on: () => void; show: boolean }[] = [
    { label: ra.hazards.length ? 'Wygeneruj ponownie' : 'Generuj', icon: 'sparkles', cls: 'btn-ai', show: canEdit && autoStates.includes(s), on: () => { if (!ra.hazards.length || window.confirm('Wygenerować ponownie? Auto-uzupełni puste dane i dosieje szkic (nie nadpisze Twoich zmian).')) streamGenerate(); } },
    { label: 'Zaakceptuj wszystkie zagrożenia', icon: 'check', cls: 'btn-secondary', show: canEdit && !ra.locked && hasProposed, on: () => run(api.acceptAllHazards(ra.id)) },
    { label: 'Generuj sekcje', icon: 'sparkles', cls: 'btn-ai', show: canAI && autoStates.includes(s) && ra.hazards.length > 0, on: generateSections },
    { label: 'Przekaż do recenzji', icon: 'arrow', cls: 'btn-primary', show: canEdit && (s === 'W_PRZYGOTOWANIU' || s === 'DO_WERYFIKACJI' || s === 'ZWROCONY'), on: submitForReview },
    { label: 'Rozpocznij recenzję', icon: 'eye', cls: 'btn-primary', show: canEdit && s === 'DO_RECENZJI', on: () => run(api.startReview(ra.id)) },
    { label: 'Zwróć do poprawek', icon: 'arrow', cls: 'btn-secondary', show: canEdit && (s === 'RECENZJA' || s === 'DO_RECENZJI'), on: () => { const r = window.prompt('Powód zwrotu do poprawek:'); if (r != null) run(api.requestChanges(ra.id, r)); } },
    { label: 'Zatwierdź i podpisz', icon: 'check', cls: 'btn-water', show: canSign && (s === 'RECENZJA' || s === 'DO_RECENZJI'), on: () => { if (window.confirm('Podpisać ocenę ryzyka? Powstanie finalny, podpisany dokument PDF.')) run(api.sign(ra.id)); } },
    { label: 'Przekaż do HydroNexis', icon: 'cloud', cls: 'btn-primary', show: canEdit && s === 'PODPISANY', on: () => run(api.handOff(ra.id)) },
    { label: 'Anuluj zlecenie', icon: 'x', cls: 'btn-ghost text-red-500', show: canEdit && !ra.locked && s !== 'ANULOWANY', on: () => { if (window.confirm('Anulować zlecenie?')) run(api.cancel(ra.id)); } },
  ];
  const visible = actions.filter(a => a.show);

  // Wariant pośredni gatingu (uwagi 7/15): Przegląd jest dostępny zawsze, ale mówi wprost, czego
  // jeszcze nie ma, i podaje drogę do właściwej zakładki — zamiast udawać, że komplet danych już jest.
  const brakDokumentow = docsCount === 0;
  const daneNiekompletne = !ra.inputData?.complete;

  return (
    <div className="grid gap-4">
      {(brakDokumentow || daneNiekompletne) && (
        <div className="card border-l-4 border-l-amber-400"><div className="card-body flex items-start gap-3 flex-wrap text-[13px]">
          <Icon name="bell" className="w-4 h-4 mt-0.5 text-amber-500 shrink-0" />
          <div className="flex-1 min-w-[220px]">
            <div className="font-semibold text-ink-900">Przegląd czeka na dane wejściowe</div>
            <div className="text-ink-500 mt-0.5">
              {brakDokumentow
                ? 'Nie wgrano jeszcze dokumentów źródłowych — to z nich system odczytuje dane do oceny.'
                : 'Dane wejściowe są niekompletne, więc automatyczna analiza pracuje na niepełnym materiale.'}
            </div>
          </div>
          {brakDokumentow && (
            <button onClick={() => onGoTab('sourceDocs')} className="btn btn-secondary btn-sm">
              <Icon name="upload" className="w-3.5 h-3.5" /> Wgraj dokumenty
            </button>
          )}
          {daneNiekompletne && (
            <button onClick={() => onGoTab('input')} className="btn btn-secondary btn-sm">
              <Icon name="edit" className="w-3.5 h-3.5" /> Uzupełnij dane wejściowe
            </button>
          )}
        </div></div>
      )}

      <div ref={mapRef}>
        <OcenaMap ra={ra} catchment={catchment} protectionZones={protZones} overlayRefreshKey={overlayKey}
          wellPoints={wells} hazardPoints={hazardPoints} canEdit={canEdit && !ra.locked}
          onPickHazard={canEdit && !ra.locked ? (lng, lat) => setPendingHazard({ lng, lat }) : undefined}
          focusGeometry={mapFocus} highlightGeometry={mapHighlight}
          onSaveGeometry={canEdit ? (geoJson, strategy) => saveCatchmentGeom(geoJson, strategy ?? 'DRAWN') : undefined} />
      </div>

      {/* uwaga 9: metryka obszaru zasilania bezpośrednio pod mapą — opisuje rysowany na niej zasięg */}
      {catchment && (
        <div className="card"><div className="card-head"><span className="card-title">Obszar zasilania</span></div>
          <div className="card-body text-[13px] grid gap-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="pill pill-water">{CATCHMENT_STRATEGY[catchment.strategy] ?? catchment.strategy}</span>
              {catchment.provisional && <span className="pill pill-amber">prowizoryczny</span>}
            </div>
            <div className="grid grid-cols-[130px_1fr] gap-y-1">
              {catchment.areaKm2 != null && <KV k="Powierzchnia" v={`${catchment.areaKm2.toFixed(2)} km²`} />}
              {catchment.radiusM != null && <KV k="Promień R" v={`${catchment.radiusM} m`} />}
            </div>
            <div className="text-[11px] text-ink-400">Pewność: {catchment.confidence} · geometria PL-1992 (EPSG:2180)</div>
          </div>
        </div>
      )}

      {pendingHazard && (
        <PlaceHazardModal meta={meta} busy={busy} coord={pendingHazard}
          onClose={() => setPendingHazard(null)}
          onSubmit={data => run(api.addHazard(ra.id, { ...data, latitude: pendingHazard.lat, longitude: pendingHazard.lng }),
            () => setPendingHazard(null))} />
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Zagrożenia" value={ra.hazards.length} />
        <Kpi label="Krytyczne" value={ra.hazards.filter(h => h.riskResidualLevel === 'Krytyczne').length} accent={ra.criticalFlag ? 'text-red-500' : undefined} />
        <Kpi label="Środki kontroli" value={controlsCount} />
        <Kpi label="Monitoring" value={ra.monitoring.length} />
        <Kpi label="Rewalidacja" value={ra.revalidationDate ? ra.revalidationDate.slice(0, 4) : '—'} />
      </div>

      {s === 'DO_WERYFIKACJI' && (
        <div className="card border-l-4 border-l-ai-400"><div className="card-body text-[13px] flex items-start gap-2">
          <Icon name="sparkles" className="w-4 h-4 mt-0.5 text-ai-500 shrink-0" />
          <div>
            <div className="font-semibold text-ink-900">Ocena wygenerowana automatycznie — do weryfikacji</div>
            <div className="text-ink-500 mt-0.5">Sprawdź dane w zakładce „Dane" i uzupełnij ewentualne luki, zweryfikuj zagrożenia i macierz ryzyka, następnie zaakceptuj zagrożenia i przekaż do recenzji. Podpis jest możliwy dopiero po akceptacji wszystkich zagrożeń.</div>
          </div>
        </div></div>
      )}

      {visible.length > 0 && (
        <div className="card"><div className="card-body grid gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[12px] text-ink-500 mr-1">Działania:</span>
            {visible.map(a => (
              <button key={a.label} onClick={a.on} disabled={busy} className={'btn btn-sm ' + a.cls}>
                <Icon name={a.icon} className="w-3.5 h-3.5" />
                {a.label === 'Generuj sekcje' && sectionsBusy ? 'Generuję sekcje…' : a.label}
              </button>
            ))}
            {/* Paczka wynikowa do CSEU — offline'owa druga strona obiegu: wodociąg wgrywa ją u siebie
                i dostaje książkę eksploatacji. Zwykły link (GET z Content-Disposition), nie mutacja. */}
            {(s === 'PODPISANY' || s === 'PRZEKAZANY') && (
              <a href={api.resultPackageUrl(ra.id)} className="btn btn-secondary btn-sm"
                title="ZIP: manifest + podpisany PDF do zaimportowania w HydroNexis (CSEU)">
                <Icon name="download" className="w-3.5 h-3.5" /> Paczka do ckes
              </a>
            )}
          </div>
          {(s === 'PODPISANY' || s === 'PRZEKAZANY') && (
            <div className="text-[11.5px] text-ink-500">
              Paczka zawiera manifest (środki kontroli z oceną ryzyka i program monitoringu) oraz podpisany
              dokument PDF. Wodociąg wgrywa ją w HydroNexis pod „Ocena ryzyka → Importuj wynik” — powstaje
              z niej harmonogram monitoringu i rejestr działań.
            </div>
          )}
          {/* uwaga 5: wynik generowania sekcji widoczny od razu, wraz z drogą do rezultatu */}
          {sectionsBusy && (
            <div className="text-[12px] text-ink-500 flex items-center gap-2">
              <Icon name="sparkles" className="w-3.5 h-3.5 text-ai-500 shrink-0" />
              Trwa generowanie sekcji dokumentu — to potrwa kilkadziesiąt sekund. Nie zamykaj strony.
            </div>
          )}
          {!sectionsBusy && sectionsInfo && (
            <div className="text-[12px] text-ink-600 flex items-center gap-2 flex-wrap">
              <Icon name="check" className="w-3.5 h-3.5 text-green-600 shrink-0" />
              Gotowe — dokument ma {sectionsInfo.count} {sectionsInfo.count === 1 ? 'wypełnioną sekcję' : 'wypełnionych sekcji'}.
              <button onClick={() => onGoTab('document')} className="btn btn-ghost btn-sm text-water-600">
                Przejdź do dokumentu
              </button>
            </div>
          )}
        </div></div>
      )}

      <GenerationSteps stages={genStages} generating={generating} evidence={evidence}
        sourceStatus={sourceStatus} canRun={canEdit && !ra.locked && autoStates.includes(s)}
        busy={busy} onRunFrom={streamGenerate} />


      {canEdit && (
        <div className="card"><div className="card-body flex items-center gap-2 flex-wrap text-[12px] text-ink-500">
          <Icon name="map" className="w-3.5 h-3.5 shrink-0" />
          <span>{ra.intakeId
            ? 'Strefa oddziaływania: użyj rzeczywistego obszaru spływu (OSW) z operatu zamiast liczonego okręgu — wczytaj poligon GeoJSON (WGS84). Przetrwa ponowną generację.'
            : 'Ocena bez ujęcia — strefę oddziaływania wgraj jako obszar OSW (poligon GeoJSON, WGS84). To jedyne źródło strefy dla oceny bez ujęcia.'}</span>
          <label className={'btn btn-sm btn-secondary ' + (busy ? 'opacity-50 pointer-events-none' : '')}>
            <Icon name="download" className="w-3.5 h-3.5" /> Wczytaj obszar (GeoJSON)
            <input type="file" accept=".geojson,.json,application/geo+json" className="hidden" disabled={busy}
              onChange={e => {
                const f = e.target.files?.[0]; (e.target as HTMLInputElement).value = '';
                if (!f) return;
                readPolygonFile(f, geomJson => saveCatchmentGeom(geomJson), m => setErr(m));
              }} />
          </label>
          <button onClick={() => setShowGeoref(true)} disabled={busy} className="btn btn-sm btn-secondary" title="Odczytaj obszar z rastrowej mapy analizy ryzyka">
            <Icon name="map" className="w-3.5 h-3.5" /> Z mapy analizy ryzyka
          </button>
          {catchment && catchment.strategy !== 'SICHARDT_BUFFER' && (
            <button onClick={resetCatchmentGeom} disabled={busy} className="btn btn-ghost btn-sm text-ink-400">
              {ra.intakeId ? 'Przywróć obszar liczony' : 'Usuń obszar'}
            </button>
          )}
        </div></div>
      )}

      <CatchmentMapPreview raId={ra.id} refreshKey={mapPreviewKey} canEdit={canEdit} />

      {showGeoref && (
        <GeoreferencerModal raId={ra.id} wells={wells}
          onSaved={geoJson => { saveCatchmentGeom(geoJson, 'DRAWN'); setShowGeoref(false); setMapPreviewKey(k => k + 1); }}
          onClose={() => setShowGeoref(false)} />
      )}

      {canEdit && !!ra.intakeId && (
        <div className="card"><div className="card-body flex items-center gap-2 flex-wrap text-[12px] text-ink-500">
          <Icon name="shield" className="w-3.5 h-3.5 shrink-0" />
          <span>Granice stref ochronnych (akt wojewody / dokumentacja): wgraj poligon GeoJSON (WGS84) — trafi
            do raportu (art. 4f ust. 2 pkt 2 lit. b), na mapę i do pola „Strefa ochronna”.</span>
          <SearchableSelect value={pzKind} onChange={setPzKind} className="btn-sm">
            <option value="POSREDNIA">teren ochrony pośredniej</option>
            <option value="BEZPOSREDNIA">teren ochrony bezpośredniej</option>
          </SearchableSelect>
          <label className={'btn btn-sm btn-secondary ' + (busy ? 'opacity-50 pointer-events-none' : '')}>
            <Icon name="download" className="w-3.5 h-3.5" /> Wczytaj granicę (GeoJSON)
            <input type="file" accept=".geojson,.json,application/geo+json" className="hidden" disabled={busy}
              onChange={e => {
                const f = e.target.files?.[0]; (e.target as HTMLInputElement).value = '';
                if (!f) return;
                readPolygonFile(f, geomJson => saveProtZone(geomJson, pzKind), m => setErr(m));
              }} />
          </label>
          {protZones.length > 0 && (
            <>
              <span className="text-ink-400">
                Wgrane: {protZones.map(z => z.kindName + (z.areaKm2 ? ` (${z.areaKm2} km²)` : '')).join(' · ')}
              </span>
              <button onClick={() => { if (window.confirm('Usunąć wgrane granice stref ochronnych?')) deleteProtZones(); }}
                disabled={busy} className="btn btn-ghost btn-sm text-ink-400">Usuń granice</button>
            </>
          )}
        </div></div>
      )}

      {canEdit && autoStates.includes(s) && (
        <div className="card"><div className="card-body flex items-center gap-2 flex-wrap text-[12px] text-ink-500">
          <Icon name="file-text" className="w-3.5 h-3.5 shrink-0" />
          <span>Masz analizę ryzyka od wodociągu? Wczytaj ją — system wyciągnie i uzupełni brakujące dane wejściowe (nie nadpisze istniejących):</span>
          <label className={'btn btn-sm btn-secondary ' + (busy ? 'opacity-50 pointer-events-none' : '')}>
            <Icon name="file-text" className="w-3.5 h-3.5" /> Wczytaj plik (PDF/DOCX)
            <input type="file" accept=".pdf,.doc,.docx" className="hidden" disabled={busy}
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) {
                  // limit rozmiaru pliku (100 MB) — zablokuj wczytanie zbyt dużego dokumentu
                  const err = validateUploadSize(f);
                  if (err) { setErr(err); (e.target as HTMLInputElement).value = ''; return; }
                  setUploadPct(0);
                  run(api.autofillFromDocument(ra.id, f, p => setUploadPct(p.percent)));
                }
                (e.target as HTMLInputElement).value = '';
              }} />
          </label>
          {busy && uploadPct !== null && <UploadProgressBar percent={uploadPct} label="Wysyłanie dokumentu…" compact className="w-full" />}
        </div></div>
      )}

      {checklist.length > 0 && (
        <div className="card">
          <div className="card-head flex items-center">
            <span className="card-title">Lista weryfikacyjna</span>
            {(() => {
              const blk = checklist.filter(i => i.blocking && !i.resolved).length;
              return blk > 0
                ? <span className="pill pill-red text-[10px] ml-2">{blk} blokujące podpis</span>
                : <span className="pill pill-green text-[10px] ml-2">gotowe do podpisu</span>;
            })()}
          </div>
          <div className="card-body grid gap-1.5 text-[13px]">
            {checklist.map((i, idx) => (
              <div key={i.gapId ?? `k${idx}`} className="flex items-start gap-2">
                <span className={'mt-1 w-2 h-2 rounded-full shrink-0 ' + (i.resolved ? 'bg-green-500' : (i.blocking ? 'bg-red-500' : 'bg-amber-400'))} />
                <div className="min-w-0">
                  <div className={i.resolved ? 'text-ink-400 line-through' : 'text-ink-800'}>{i.label}</div>
                  {i.detail && <div className="text-[11px] text-ink-400">{i.detail}</div>}
                </div>
                <span className="ml-auto flex items-center gap-1.5 shrink-0">
                  {i.blocking && !i.resolved && <span className="pill pill-red text-[9px]">blokuje</span>}
                  {canEdit && !ra.locked && i.gapId != null && (
                    <button onClick={() => run(api.resolveGap(ra.id, i.gapId as number, !i.resolved))} disabled={busy}
                      className={'btn btn-sm ' + (i.resolved ? 'btn-ghost' : 'btn-secondary')}>
                      {i.resolved ? 'Cofnij' : 'Rozstrzygnij'}
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Karty jedna pod drugą (pełna szerokość); metryka obszaru zasilania siedzi TUŻ POD MAPĄ
          (uwaga 9), bo opisuje to, co użytkownik właśnie na niej widzi */}
      {(gaps.length > 0 || overlay.length > 0) && (
        <div className="grid gap-4">
          {pressureTypes.length > 0 && (
            <div className="card"><div className="card-head">
              <span className="card-title">Wykryte presje w zlewni ({pressureTypes.reduce((n, t) => n + t.obiektow, 0)})</span>
              <span className="text-[11px] text-ink-400 ml-2">pogrupowane po typie · kliknij pozycję, aby zobaczyć szczegóły i wskazać obiekt na mapie</span>
            </div>
              <div className="card-body text-[13px] grid gap-2">
                {pressureTypes.map(t => {
                  const zwiniety = collapsedTypes.has(t.sourceLayer);
                  return (
                    <div key={t.sourceLayer} className="grid gap-1 min-w-0">
                      {/* NAGŁÓWEK TYPU — inwentarz czyta się sektorami, nie płaską listą */}
                      <button type="button"
                        onClick={() => setCollapsedTypes(prev => {
                          const n = new Set(prev);
                          if (n.has(t.sourceLayer)) n.delete(t.sourceLayer); else n.add(t.sourceLayer);
                          return n;
                        })}
                        className="flex items-center gap-2 min-w-0 text-left border-b border-ink-100 pb-1">
                        <Icon name="chevron-down" className={'w-3.5 h-3.5 text-ink-400 shrink-0 transition-transform ' + (zwiniety ? '-rotate-90' : '')} />
                        <span className="pill pill-water text-[10px] shrink-0" title={t.sourceLayer}>{t.layerLabel}</span>
                        <span className="text-ink-400 text-[11px] shrink-0">{t.obiektow} {odmienObiekty(t.obiektow)}</span>
                        <span className="text-ink-400 text-[11px] ml-auto whitespace-nowrap shrink-0">
                          {t.wZlewni > 0 ? `${t.wZlewni} w zlewni` : (t.distanceM != null ? `najbliżej ${Math.round(t.distanceM)} m` : '')}
                        </span>
                      </button>

                      {!zwiniety && t.grupy.map(g => {
                        const klucz = g.sourceLayer + '::' + g.name;
                        const rozwinieta = expandedPressure === klucz;
                        const jednoznaczna = g.members.length === 1;
                        const wybranyWGrupie = g.members.some(m => m.overlayId === selectedPressure);
                        return (
                          <div key={klucz} className="grid gap-1 min-w-0 pl-5">
                            <button type="button"
                              onClick={() => jednoznaczna
                                ? wybierzPresje(selectedPressure === g.members[0].overlayId ? null : g.members[0].overlayId)
                                : setExpandedPressure(x => x === klucz ? null : klucz)}
                              className={'flex items-center gap-2 min-w-0 text-left rounded px-1 -mx-1 py-0.5 hover:bg-ink-50 '
                                + (wybranyWGrupie ? 'bg-water-50' : '')}>
                              {/* min-w-0 jest WARUNKIEM działania truncate: bez niego flex item ma min-width:auto,
                                  nie kurczy się poniżej treści i wypycha status („w zlewni") poza kartę */}
                              <span className="text-ink-700 truncate min-w-0" title={g.name}>{g.name}{g.count > 1 ? ` ×${g.count}` : ''}</span>
                              <span className="text-ink-400 ml-auto whitespace-nowrap shrink-0">{g.intersects ? 'w zlewni' : (g.distanceM != null ? `${Math.round(g.distanceM)} m` : '')}</span>
                              {!jednoznaczna && (
                                <Icon name="chevron-down" className={'w-3.5 h-3.5 text-ink-400 shrink-0 transition-transform ' + (rozwinieta ? 'rotate-180' : '')} />
                              )}
                            </button>

                            {/* szczegóły BEZPOŚREDNIO pod klikniętą pozycją — grupa jednoelementowa */}
                            {jednoznaczna && wybranyWGrupie && (
                              <PressureDetailPanel raId={ra.id} overlayId={g.members[0].overlayId}
                                onClose={() => wybierzPresje(null)}
                                onLoaded={d => setMapHighlight(d?.geoJson ?? null)}
                                onShowOnMap={d => { if (d.geoJson) pokazNaMapie(d.geoJson); }} />
                            )}

                            {/* grupa liczy nawet setki obiektów — pokazujemy najbliższe, reszta zostaje zliczona */}
                            {rozwinieta && (
                              <div className="pl-5 grid gap-0.5">
                                {g.members.slice(0, 20).map((m, i) => (
                                  <Fragment key={m.overlayId}>
                                    <button type="button"
                                      onClick={() => wybierzPresje(selectedPressure === m.overlayId ? null : m.overlayId)}
                                      className={'flex items-center gap-2 min-w-0 text-left text-[12px] rounded px-1 py-0.5 hover:bg-ink-50 '
                                        + (selectedPressure === m.overlayId ? 'bg-water-50' : '')}>
                                      <span className="text-ink-400 shrink-0 font-mono">#{i + 1}</span>
                                      <span className="text-ink-600 truncate min-w-0">obiekt {m.overlayId}</span>
                                      <span className="text-ink-400 ml-auto whitespace-nowrap shrink-0">
                                        {m.intersects ? 'w zlewni' : (m.distanceM != null ? `${Math.round(m.distanceM)} m` : '')}
                                      </span>
                                    </button>
                                    {/* szczegóły BEZPOŚREDNIO pod klikniętym obiektem, nie na końcu listy */}
                                    {selectedPressure === m.overlayId && (
                                      <PressureDetailPanel raId={ra.id} overlayId={m.overlayId}
                                        onClose={() => wybierzPresje(null)}
                                        onLoaded={d => setMapHighlight(d?.geoJson ?? null)}
                                        onShowOnMap={d => { if (d.geoJson) pokazNaMapie(d.geoJson); }} />
                                    )}
                                  </Fragment>
                                ))}
                                {g.members.length > 20 && (
                                  <div className="text-[11px] text-ink-400 px-1">
                                    …i {g.members.length - 20} dalszych obiektów (pokazano 20 najbliższych)
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {gaps.length > 0 && (
            <div className="card"><div className="card-head"><span className="card-title">Luki do uzupełnienia ({gaps.length})</span></div>
              <div className="card-body text-[13px] grid gap-1">
                {gaps.map(g => (
                  <div key={g.id} className="flex items-center gap-2">
                    <span className="text-ink-700">{g.label}</span>
                    <span className="pill pill-gray text-[10px] ml-auto" title={g.reason ?? ''}>{g.requiredFrom}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <SourceStatusPanel items={sourceStatus} raId={ra.id} canEdit={canEdit} canUpload={canUpload}
        hasIntake={!!ra.intakeId}
        hasCoords={!!ra.intakeId || (ra.latitude != null && ra.longitude != null)}
        onUpdated={onSourceStatus} onError={setErr} onFetched={() => setOverlayKey(k => k + 1)} />

      {evidence.length > 0 && (
        <div className="card">
          <div className="card-head flex items-center">
            <span className="card-title">Odczytane dane — proweniencja ({evidence.length})</span>
            <span className="text-[11px] text-ink-400 ml-2">źródło i data odczytu każdej automatycznie pozyskanej wartości</span>
          </div>
          <div className="card-body !p-0 overflow-x-auto">
            <table className="table-hw">
              <thead><tr><th>Dana</th><th>Wartość</th><th className="w-52">Źródło (API/zbiór)</th><th className="w-28">Odczytano</th></tr></thead>
              <tbody>
                {evidence.map(f => (
                  <tr key={f.id}>
                    <td className="text-ink-800">{f.label ?? f.factKey}</td>
                    <td className="text-[12px] text-ink-600">{f.value ?? '—'}{f.distanceM != null ? ` · ${Math.round(f.distanceM)} m` : ''}</td>
                    <td>{f.sourceDataset
                      ? <span className="pill pill-water text-[10px]">{f.sourceDataset}</span>
                      : <span className="text-ink-400 text-[11px]">—</span>}</td>
                    <td className="text-[11px] text-ink-400 font-mono">{f.fetchedAt ? f.fetchedAt.slice(0, 10) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div className="card"><div className="card-head"><span className="card-title">Metryka zlecenia</span></div>
          <div className="card-body text-[13px] grid grid-cols-[130px_1fr] gap-y-1.5">
            <KV k="Dyspozytor" v={ra.dispatcherName} />
            <KV k="Specjalista" v={ra.specialistName} />
            <KV k="Osoba uprawniona" v={ra.reviewerName} />
            <KV k="Termin ustawowy" v={ra.deadline} />
            <KV k="Wymagane uprawnienia" v={ra.requiredSignerQualification} />
            <KV k="Ocena obszaru zasilania (4f)" v={ra.sourceAssessmentNumber} />
            {ra.hydronexisOrderId && <KV k="Zlecenie HydroNexis" v={ra.hydronexisOrderId} />}
            {ra.signedAt && <KV k="Podpisano" v={`${ra.signerName ?? ''} · ${ra.signedAt.slice(0, 10)}`} />}
            {ra.revalidationDate && <KV k="Rewalidacja (+6 lat)" v={ra.revalidationDate} />}
          </div>
        </div>

        <Assignments ra={ra} meta={meta} busy={busy} run={run} canAssign={canAssign} />
      </div>

      {ra.notes && (
        <div className="card"><div className="card-head"><span className="card-title">Uwagi</span></div>
          <div className="card-body text-[13px] whitespace-pre-wrap text-ink-700">{ra.notes}</div>
        </div>
      )}
      <button onClick={reload} disabled={busy} className="btn btn-ghost btn-sm self-start text-ink-400 disabled:opacity-50"><Icon name="download" className="w-3.5 h-3.5" /> Odśwież</button>
    </div>
  );
}

/** Polska odmiana rzeczownika „obiekt": 1 obiekt, 2–4 obiekty, 5+ obiektów (z wyjątkiem 12–14). */
function odmienObiekty(n: number): string {
  if (n === 1) return 'obiekt';
  const dziesiatki = n % 100;
  const jednosci = n % 10;
  if (jednosci >= 2 && jednosci <= 4 && !(dziesiatki >= 12 && dziesiatki <= 14)) return 'obiekty';
  return 'obiektów';
}

function Assignments({ ra, meta, busy, run, canAssign }: {
  ra: RiskAssessment; meta: RiskMeta | null; busy: boolean; run: (p: Promise<RiskAssessment>, onSuccess?: () => void) => void; canAssign: boolean;
}) {
  const [uid, setUid] = useState('');
  const [role, setRole] = useState('SPECJALISTA');
  return (
    <div className="card"><div className="card-head"><span className="card-title">Zespół (dostęp per-zlecenie)</span></div>
      <div className="card-body">
        {ra.assignments.length === 0 && <div className="text-ink-400 text-[12px] mb-2">Brak dodatkowych przypisań.</div>}
        {ra.assignments.map(a => (
          <div key={a.id} className="flex items-center gap-2 text-[13px] py-1 border-b border-ink-50 last:border-0">
            <span className="font-semibold text-ink-800">{a.userName}</span>
            <span className="pill pill-gray">{a.roleName}</span>
            {a.note && <span className="text-ink-400 text-[11px]">{a.note}</span>}
            {canAssign && <button onClick={() => run(api.removeAssignment(ra.id, a.id))} disabled={busy} className="ml-auto btn btn-ghost btn-icon text-ink-400"><Icon name="x" className="w-3.5 h-3.5" /></button>}
          </div>
        ))}
        {canAssign && meta && (
          <div className="flex items-end gap-2 mt-3">
            <div className="flex-1"><SearchableSelect value={uid} onChange={setUid} placeholder="Osoba…">
              <option value="">— wybierz —</option>
              {meta.assignableUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </SearchableSelect></div>
            <div className="w-40"><SearchableSelect value={role} onChange={setRole}>
              {meta.reviewerRoles.map(r => <option key={r.code} value={r.code}>{r.name}</option>)}
            </SearchableSelect></div>
            <button disabled={!uid || busy} onClick={() => { run(api.addAssignment(ra.id, Number(uid), role)); setUid(''); }} className="btn btn-secondary btn-sm">Dodaj</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== Dane wejściowe ====================
/** Grupa pól, pod którą ląduje karta danych ujęcia i lista studni (uwaga 8). */
const GROUP_BASE = 'Dane podstawowe';

function InputTab({ ra, meta, busy, run, canEdit, canAI, setErr, onWellsChanged, saveSpatialData, extractIntakeDoc }: {
  ra: RiskAssessment; meta: RiskMeta | null; busy: boolean; run: (p: Promise<RiskAssessment>, onSuccess?: () => void) => void; canEdit: boolean; canAI: boolean;
  setErr: (m: string | null) => void; onWellsChanged: (w: RiskAssessmentWell[]) => void;
  saveSpatialData: (d: Record<string, unknown>) => void; extractIntakeDoc: (f: File) => void;
}) {
  const [data, setData] = useState<Record<string, string>>({});
  const [docs, setDocs] = useState<RiskSourceDocument[]>([]);
  useEffect(() => {
    try { setData(JSON.parse(ra.inputData?.dataJson || '{}')); } catch { setData({}); }
  }, [ra.inputData?.id]);
  // lista dokumentów źródłowych do odczytu danych (uwaga 12) — odświeżana po każdym zapisie danych
  useEffect(() => { api.sourceDocuments(ra.id).then(setDocs).catch(() => {}); }, [ra.id, ra.inputData?.id]);

  const groups = useMemo(() => {
    const g: Record<string, RiskMeta['inputFields']> = {};
    for (const f of meta?.inputFields ?? []) (g[f.group] ||= []).push(f);
    return g;
  }, [meta]);

  const missing = useMemo(() => {
    try { return new Set<string>(JSON.parse(ra.inputData?.missingFieldsJson || '[]')); } catch { return new Set<string>(); }
  }, [ra.inputData?.missingFieldsJson]);

  const save = () => run(api.saveInput(ra.id, JSON.stringify(data)));
  if (!meta) return <div className="text-ink-400">Ładowanie…</div>;

  const complete = ra.inputData?.complete;
  return (
    <div className="grid gap-4">
      <div className={'card ' + (complete ? '' : '')}>
        <div className="card-body flex items-center gap-3 flex-wrap">
          <span className={'pill ' + (complete ? 'pill-green' : 'pill-amber')}>
            <span className="pill-dot" />{complete ? 'Dane kompletne' : 'Dane niekompletne'}
          </span>
          <span className="text-[12px] text-ink-500">
            {complete ? 'Można wygenerować szkic oceny (zakładka Zagrożenia).'
              : `Uzupełnij pola krytyczne oznaczone gwiazdką dla warstwy ${ra.layerName}.`}
          </span>
          {ra.inputData && <span className="text-[11px] text-ink-400 ml-auto">wersja danych: v{ra.inputData.version} · źródło: {ra.inputData.source}</span>}
        </div>
      </div>

      {/* uwaga 12: dwie drogi do tych samych pól — wpisać ręcznie albo kazać systemowi odczytać
          z wgranej dokumentacji. Odczyt uzupełnia WYŁĄCZNIE puste pola, więc nie kasuje pracy eksperta. */}
      {canEdit && canAI && (
        <div className="card">
          <div className="card-head">
            <span className="card-title">Odczyt z dokumentów źródłowych</span>
            <span className="text-[11px] text-ink-400 ml-2">uzupełnia tylko puste pola — Twoje wpisy zostają nietknięte</span>
          </div>
          <div className="card-body grid gap-1.5 text-[12px]">
            {docs.length === 0
              ? <div className="text-ink-400">Brak dokumentów. Wgraj dokumentację w zakładce „Dokumenty źródłowe”, żeby odczytać z niej dane.</div>
              : docs.map(d => (
                <div key={d.id} className="flex items-center gap-2 min-w-0">
                  <Icon name="file-text" className="w-3.5 h-3.5 text-ink-400 shrink-0" />
                  <span className="truncate min-w-0 text-ink-700">{d.name}</span>
                  <button onClick={() => run(api.autofillFromSourceDocument(ra.id, d.id))} disabled={busy}
                    className="btn btn-ai btn-sm ml-auto shrink-0">
                    <Icon name="sparkles" className="w-3.5 h-3.5" /> Odczytaj dane
                  </button>
                </div>
              ))}
          </div>
        </div>
      )}

      {Object.entries(groups).map(([group, fields]) => {
        // uwaga 11: pokazujemy WYŁĄCZNIE pola tej warstwy — do tej pory pola opcjonalne (pusty
        // requiredFor) przeciekały do wszystkich warstw, więc ocena 4f pytała o instalację wewnętrzną
        const relevant = fields.filter(f => f.appliesTo.length === 0 || f.appliesTo.includes(ra.layer));
        if (relevant.length === 0) return null;
        return (
          <Fragment key={group}>
          <div className="card">
            <div className="card-head"><span className="card-title">{group}</span></div>
            <div className="card-body grid gap-3">
              {relevant.map(f => {
                const required = f.requiredFor.includes(ra.layer);
                const isMissing = missing.has(f.label);
                return (
                  <label key={f.key} className="field">
                    <span className="field-label">
                      {f.label}{required && <span className="text-red-500"> *</span>}
                      {isMissing && <span className="text-red-500 text-[10px] ml-1">— wymagane</span>}
                    </span>
                    {f.kind === 'TEXTAREA'
                      ? <textarea className={'textarea min-h-[64px] ' + (isMissing ? 'border-red-300' : '')} value={data[f.key] ?? ''} disabled={!canEdit}
                          onChange={e => setData({ ...data, [f.key]: e.target.value })} />
                      : <input type={f.kind === 'NUMBER' ? 'number' : 'text'} className={'input ' + (isMissing ? 'border-red-300' : '')} value={data[f.key] ?? ''} disabled={!canEdit}
                          onChange={e => setData({ ...data, [f.key]: e.target.value })} />}
                    {f.hint && <span className="field-hint">{f.hint}</span>}
                  </label>
                );
              })}
            </div>
          </div>
          {/* uwaga 8: dane ujęcia i studnie to DANE WEJŚCIOWE — mieszkają tu, a nie w „Przeglądzie",
              żeby nie było dwóch miejsc z tą samą informacją */}
          {group === GROUP_BASE && !ra.intakeId && (
            <SpatialEditCard ra={ra} busy={busy} canEdit={canEdit} onSave={saveSpatialData} onExtract={extractIntakeDoc} setErr={setErr} />
          )}
          {group === GROUP_BASE && (
            <WellsPanel raId={ra.id} hasIntake={!!ra.intakeId} canEdit={canEdit} onWellsChanged={onWellsChanged} />
          )}
          </Fragment>
        );
      })}

      {canEdit && (
        <button onClick={save} disabled={busy} className="btn btn-primary self-start">
          <Icon name="check" className="w-3.5 h-3.5" /> Zapisz i sprawdź kompletność
        </button>
      )}
    </div>
  );
}

// ==================== Zagrożenia ====================
function HazardsTab({ ra, meta, busy, run, canEdit, canAI }: {
  ra: RiskAssessment; meta: RiskMeta | null; busy: boolean; run: (p: Promise<RiskAssessment>, onSuccess?: () => void) => void; canEdit: boolean; canAI: boolean;
}) {
  const [mode, setMode] = useState<'initial' | 'residual'>('residual');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [adding, setAdding] = useState(false);
  const toggle = (id: number) => setExpanded(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // Uwaga 18 (30.07.2026): każda zmiana P×D woła engine.recompute, który przenumerowuje `priority`,
  // więc edytowany wiersz „uciekał" spod kursora. Kolejność wyświetlania jest zamrożona: nowe pozycje
  // dopisujemy na koniec, a przesortowanie wg ryzyka następuje dopiero na żądanie / po wejściu w zakładkę.
  const byPriority = (list: RiskHazard[]) => [...list].sort((a, b) => a.priority - b.priority).map(h => h.id);
  const [orderIds, setOrderIds] = useState<number[]>(() => byPriority(ra.hazards));
  useEffect(() => {
    setOrderIds(prev => {
      const live = byPriority(ra.hazards);
      const liveSet = new Set(live);
      const keptSet = new Set(prev.filter(id => liveSet.has(id)));
      const added = live.filter(id => !keptSet.has(id));
      // bez zmian w składzie listy zwracamy poprzednią tablicę — inaczej efekt zapętliłby render
      if (added.length === 0 && keptSet.size === prev.length) return prev;
      return [...prev.filter(id => keptSet.has(id)), ...added];
    });
  }, [ra.hazards]);
  const orderRank = useMemo(() => new Map(orderIds.map((id, i) => [id, i])), [orderIds]);
  const orderStale = useMemo(
    () => byPriority(ra.hazards).some((id, i) => orderIds[i] !== id),
    [ra.hazards, orderIds],
  );

  return (
    <div className="grid gap-4">
      <div className="card"><div className="card-body flex items-center gap-2 flex-wrap">
        {canEdit && ra.hazards.length === 0 && (
          <button onClick={() => run(api.generateDraft(ra.id))} disabled={busy} className="btn btn-primary btn-sm">
            <Icon name="sparkles" className="w-3.5 h-3.5" /> Generuj szkic z biblioteki
          </button>
        )}
        {canEdit && (
          <button onClick={() => run(api.generateDraft(ra.id))} disabled={busy} className="btn btn-secondary btn-sm" title="Dosiewa brakujące pozycje z biblioteki wzorców">
            <Icon name="download" className="w-3.5 h-3.5" /> Uzupełnij z biblioteki
          </button>
        )}
        {canEdit && canAI && (
          <button onClick={() => run(api.aiSuggestHazards(ra.id))} disabled={busy} className="btn btn-ai btn-sm" title="Uzupełnia brakujące zagrożenia na podstawie danych oceny">
            <Icon name="sparkles" className="w-3.5 h-3.5" /> Uzupełnij
          </button>
        )}
        {ra.hazards.length > 1 && (
          <button onClick={() => setOrderIds(byPriority(ra.hazards))} disabled={!orderStale}
            className={'btn btn-sm ' + (orderStale ? 'btn-secondary' : 'btn-ghost')}
            title={orderStale
              ? 'Kolejność jest zamrożona na czas edycji — kliknij, aby ustawić wiersze wg aktualnego ryzyka'
              : 'Wiersze są ustawione wg aktualnego ryzyka'}>
            <Icon name="arrow" className="w-3.5 h-3.5 rotate-90" /> Sortuj wg ryzyka
          </button>
        )}
        <div className="ml-auto flex items-center gap-1">
          <span className="text-[11px] text-ink-400">Macierz:</span>
          <button onClick={() => setMode('initial')} className={'btn btn-sm ' + (mode === 'initial' ? 'btn-primary' : 'btn-ghost')}>początkowe</button>
          <button onClick={() => setMode('residual')} className={'btn btn-sm ' + (mode === 'residual' ? 'btn-primary' : 'btn-ghost')}>rezydualne</button>
        </div>
      </div></div>

      {ra.hazards.length > 0 && (
        <div className="card"><div className="card-body flex gap-6 flex-wrap items-start">
          <RiskMatrix hazards={ra.hazards} mode={mode} levels={meta?.riskLevels}
            severityScale={meta?.methodology?.severityScale} />
          <div className="flex-1 min-w-[180px]">
            <div className="text-[13px] font-semibold mb-2">
              Poziomy ryzyka{meta?.methodology?.severityScale === 'KWADRATOWA' ? ' (dotkliwość ważona kwadratowo)' : ''}
            </div>
            {/* fallback = te same pasma, na których rysuje się macierz bez meta — legenda nigdy nie jest pusta */}
            {(meta?.riskLevels ?? FALLBACK_BANDS).map(l => (
              <div key={l.code ?? l.name} className="flex items-center gap-2 text-[12px] mb-1">
                <span className="w-3.5 h-3.5 rounded" style={{ background: l.hex }} />
                <span className="text-ink-600">{l.name} ({l.min}–{l.max})</span>
              </div>
            ))}
          </div>
        </div></div>
      )}

      {ra.hazards.length === 0 && (
        <div className="card"><div className="card-body text-center text-ink-400 py-6">
          Brak zagrożeń. {canEdit ? 'Wygeneruj szkic z biblioteki wzorców.' : ''}
        </div></div>
      )}

      {ra.hazards.length > 0 && (
        <div className="card"><div className="card-body !p-0 overflow-x-auto">
          <table className="table-hw">
            <thead><tr>
              <th className="w-8">#</th><th className="w-32">Kategoria</th><th>Zagrożenie i zdarzenie niebezpieczne</th>
              <th className="w-24 text-center">Początk.</th><th className="w-24 text-center">Rezyd.</th>
              <th className="w-20 text-center">Poziom</th><th className="w-8"></th>
            </tr></thead>
            <tbody>
              {(() => {
                // F3 — inwentarz SEKTOROWY (układ arkuszy LAWA / DVGW W 1004): zagrożenia grupowane po
                // sektorze, wewnątrz grupy wg ZAMROŻONEJ kolejności (uwaga 18) zamiast żywego priorytetu;
                // NULL → „pozostałe / ogólnosystemowe"
                const rank = (h: RiskHazard) => orderRank.get(h.id) ?? Number.MAX_SAFE_INTEGER;
                const sorted = [...ra.hazards].sort((a, b) => rank(a) - rank(b));
                const groups = new Map<string, typeof sorted>();
                for (const h of sorted) {
                  const k = h.sector || 'pozostałe / ogólnosystemowe';
                  const list = groups.get(k) ?? [];
                  if (list.length === 0) groups.set(k, list);
                  list.push(h);
                }
                return [...groups.entries()].map(([sec, list]) => (
                  <Fragment key={sec}>
                    <tr className="bg-ink-50/80">
                      <td colSpan={7} className="!py-1.5 text-[10px] uppercase tracking-wide text-ink-500">
                        Sektor: {sec} ({list.length})
                      </td>
                    </tr>
                    {list.map(h => (
                      <HazardRow key={h.id} ra={ra} h={h} meta={meta} busy={busy} run={run} canEdit={canEdit}
                        expanded={expanded.has(h.id)} onToggle={() => toggle(h.id)} />
                    ))}
                  </Fragment>
                ));
              })()}
            </tbody>
          </table>
        </div></div>
      )}

      {canEdit && (adding
        ? <AddHazardForm ra={ra} meta={meta} busy={busy} run={run} onDone={() => setAdding(false)} />
        : <button onClick={() => setAdding(true)} className="btn btn-secondary btn-sm self-start"><Icon name="plus" className="w-3.5 h-3.5" /> Dodaj zagrożenie ręcznie</button>)}
    </div>
  );
}

function HazardRow({ ra, h, meta, busy, run, canEdit, expanded, onToggle }: {
  ra: RiskAssessment; h: RiskHazard; meta: RiskMeta | null; busy: boolean;
  run: (p: Promise<RiskAssessment>, onSuccess?: () => void) => void; canEdit: boolean; expanded: boolean; onToggle: () => void;
}) {
  const upd = (patch: Record<string, unknown>) => run(api.updateHazard(ra.id, h.id, patch));
  const num = (v: number, on: (n: number) => void) => (
    <select value={v} disabled={!canEdit || busy} onClick={e => e.stopPropagation()} onChange={e => on(Number(e.target.value))}
      className="border border-ink-200 rounded px-1 py-0.5 text-[12px] bg-white">
      {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
    </select>
  );
  return (
    <>
      <tr onClick={onToggle} className="align-top">
        <td className="font-mono text-ink-400">{h.priority}</td>
        <td><span className="pill" style={{ background: h.categoryColor + '22', color: h.categoryColor }}>{h.categoryName}</span></td>
        <td>
          <div className="font-semibold text-ink-900 flex items-center gap-1.5 flex-wrap">
            {h.title}
            {h.aiGenerated && <span className="pill pill-ai text-[9px]">AI</span>}
            {h.sourceLayer && <span className="pill pill-water text-[9px]"
              title={`Wyprowadzone z overlay presji: ${h.sourceLayer}${h.distanceM != null ? ` · ${Math.round(h.distanceM)} m` : ''}${h.confidence ? ` · pewność ${h.confidence}` : ''}`}>
              {h.sourceLayer}{h.distanceM != null ? ` ${Math.round(h.distanceM)} m` : ''}</span>}
          </div>
          {h.description && <div className="text-[11px] text-ink-400 mt-0.5 line-clamp-2">{h.description}</div>}
          {(h.hazardCarrier || h.triggerEvent) && (
            <div className="text-[11px] text-ink-500 mt-0.5">
              {h.hazardCarrier && <span><span className="text-ink-400">Nośnik:</span> {h.hazardCarrier}</span>}
              {h.hazardCarrier && h.triggerEvent && <span className="text-ink-300"> · </span>}
              {h.triggerEvent && <span><span className="text-ink-400">Zdarzenie:</span> {h.triggerEvent}</span>}
            </div>
          )}
        </td>
        <td className="text-center whitespace-nowrap">{num(h.probabilityInitial, n => upd({ probabilityInitial: n }))}×{num(h.severityInitial, n => upd({ severityInitial: n }))}<div className="text-[10px] font-mono mt-0.5" style={{ color: h.riskInitialColor }}>{h.riskInitial}</div></td>
        <td className="text-center whitespace-nowrap">{num(h.probabilityResidual, n => upd({ probabilityResidual: n }))}×{num(h.severityResidual, n => upd({ severityResidual: n }))}<div className="text-[10px] font-mono mt-0.5" style={{ color: h.riskResidualColor }}>{h.riskResidual}</div></td>
        <td className="text-center"><span className="pill text-white" style={{ background: h.riskResidualColor }}>{h.riskResidualLevel}</span></td>
        <td onClick={e => e.stopPropagation()}>
          {canEdit && <button onClick={() => { if (window.confirm('Usunąć zagrożenie?')) run(api.deleteHazard(ra.id, h.id)); }} disabled={busy} className="btn btn-ghost btn-icon text-ink-400"><Icon name="x" className="w-3.5 h-3.5" /></button>}
        </td>
      </tr>
      {expanded && (
        <tr><td colSpan={7} className="bg-ink-50/60">
          <HazardJustification ra={ra} h={h} busy={busy} run={run} canEdit={canEdit} />
          <ControlList ra={ra} h={h} meta={meta} busy={busy} run={run} canEdit={canEdit} />
        </td></tr>
      )}
    </>
  );
}

/**
 * Uzasadnienie przyjętych P i D (uwaga 21 z 30.07.2026). AI proponuje przy generacji, ekspert
 * weryfikuje i poprawia. Zapis jawnym przyciskiem — pole bywa długie, a autozapis przy każdym
 * znaku wysyłałby połowę zdania do bazy i do dokumentu.
 */
function HazardJustification({ ra, h, busy, run, canEdit }: {
  ra: RiskAssessment; h: RiskHazard; busy: boolean;
  run: (p: Promise<RiskAssessment>, onSuccess?: () => void) => void; canEdit: boolean;
}) {
  const zapisane = h.riskJustification ?? '';
  const [text, setText] = useState(zapisane);
  useEffect(() => { setText(h.riskJustification ?? ''); }, [h.id, h.riskJustification]);
  const dirty = text.trim() !== zapisane.trim();
  return (
    <div className="px-3 pt-3">
      <div className="text-[12px] font-semibold text-ink-600 mb-1 flex items-center gap-2">
        Uzasadnienie oceny (P i D)
        {!zapisane && <span className="pill pill-amber text-[9px]">brak</span>}
        {h.aiGenerated && zapisane && <span className="pill pill-ai text-[9px]">propozycja AI</span>}
      </div>
      {canEdit ? (
        <div className="grid gap-1.5">
          <textarea value={text} onChange={e => setText(e.target.value)} disabled={busy}
            className="textarea min-h-[56px] text-[12px] bg-white"
            placeholder="Dlaczego przyjęto takie prawdopodobieństwo i dotkliwość — przesłanki, dane, odległości, historia incydentów…" />
          {dirty && (
            <div className="flex gap-2">
              <button onClick={() => run(api.updateHazard(ra.id, h.id, { riskJustification: text }))}
                disabled={busy} className="btn btn-primary btn-sm">Zapisz uzasadnienie</button>
              <button onClick={() => setText(zapisane)} className="btn btn-ghost btn-sm">Przywróć</button>
            </div>
          )}
        </div>
      ) : (
        <div className="text-[12px] text-ink-600">{zapisane || <span className="text-ink-400">—</span>}</div>
      )}
    </div>
  );
}

function ControlList({ ra, h, meta, busy, run, canEdit }: {
  ra: RiskAssessment; h: RiskHazard; meta: RiskMeta | null; busy: boolean; run: (p: Promise<RiskAssessment>, onSuccess?: () => void) => void; canEdit: boolean;
}) {
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);   // uwaga 17: edycja proponowanego środka kontroli
  const [type, setType] = useState('TECHNICZNE');
  const [desc, setDesc] = useState('');
  const [resp, setResp] = useState('DOSTAWCA');
  const [respOther, setRespOther] = useState('');   // nazwa podmiotu przy „Inny podmiot" (uwaga 19)
  const [existing, setExisting] = useState(false);
  const [deadline, setDeadline] = useState('');
  const add = () => run(api.addControl(ra.id, h.id, {
    type, description: desc, responsibility: resp,
    responsibilityOther: resp === 'INNY' ? respOther : undefined,
    existing, implementationDeadline: deadline || undefined,
  }), () => { setDesc(''); setDeadline(''); setRespOther(''); setAdding(false); });
  return (
    <div className="p-3">
      <div className="text-[12px] font-semibold text-ink-600 mb-2">Środki kontroli</div>
      {h.controls.length === 0 && <div className="text-[12px] text-ink-400 mb-2">Brak środków.</div>}
      <div className="grid gap-1.5 mb-2">
        {h.controls.map(c => (editId === c.id ? (
          <ControlEditForm key={c.id} ra={ra} h={h} c={c} meta={meta} busy={busy} run={run} onDone={() => setEditId(null)} />
        ) : (
          <div key={c.id} className="flex items-start gap-2 text-[12px] bg-white border border-ink-100 rounded px-2 py-1.5">
            <span className="pill pill-gray flex-shrink-0">{c.typeName}</span>
            <span className={'pill flex-shrink-0 ' + (c.existing ? 'pill-green' : 'pill-amber')}>{c.existing ? 'istniejący' : 'proponowany'}</span>
            <div className="flex-1">
              <div className="text-ink-800">{c.description}</div>
              <div className="text-ink-400 text-[10px]">{c.responsibilityName}{c.implementationDeadline ? ` · termin ${c.implementationDeadline}` : ''}{c.aiGenerated ? ' · AI' : ''}</div>
            </div>
            {canEdit && (
              <div className="flex gap-0.5">
                <button onClick={() => run(api.updateControl(ra.id, h.id, c.id, { existing: !c.existing }))} disabled={busy} className="btn btn-ghost btn-icon text-ink-400" title="Przełącz istniejący/proponowany"><Icon name="check" className="w-3.5 h-3.5" /></button>
                <button onClick={() => setEditId(c.id)} disabled={busy} className="btn btn-ghost btn-icon text-ink-400" title="Edytuj środek kontroli"><Icon name="edit" className="w-3.5 h-3.5" /></button>
                <button onClick={() => run(api.deleteControl(ra.id, h.id, c.id))} disabled={busy} className="btn btn-ghost btn-icon text-ink-400"><Icon name="x" className="w-3.5 h-3.5" /></button>
              </div>
            )}
          </div>
        )))}
      </div>
      {canEdit && (adding ? (
        <div className="bg-white border border-ink-100 rounded p-2 grid gap-2">
          <div className="grid grid-cols-[140px_1fr] gap-2">
            <SearchableSelect value={type} onChange={setType} className="btn-sm">
              {(meta?.controlTypes ?? []).map(t => <option key={t.code} value={t.code}>{t.name}</option>)}
            </SearchableSelect>
            <input className="input" value={desc} onChange={e => setDesc(e.target.value)} placeholder="Opis środka kontroli" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <SearchableSelect value={resp} onChange={setResp} className="btn-sm">
              {(meta?.responsibilities ?? []).map(r => <option key={r.code} value={r.code}>{r.name}</option>)}
            </SearchableSelect>
            {resp === 'INNY' && (
              <input className="input w-56" value={respOther} onChange={e => setRespOther(e.target.value)}
                placeholder="Nazwa podmiotu (np. Gmina Pakosław)" />
            )}
            <label className="flex items-center gap-1 text-[12px]"><input type="checkbox" checked={existing} onChange={e => setExisting(e.target.checked)} /> istniejący</label>
            <input type="date" className="input w-40" value={deadline} onChange={e => setDeadline(e.target.value)} />
            <button onClick={add} disabled={!desc || busy || (resp === 'INNY' && !respOther.trim())} className="btn btn-primary btn-sm">Dodaj</button>
            <button onClick={() => setAdding(false)} className="btn btn-ghost btn-sm">Anuluj</button>
          </div>
        </div>
      ) : <button onClick={() => setAdding(true)} className="btn btn-ghost btn-sm text-water-600"><Icon name="plus" className="w-3.5 h-3.5" /> Dodaj środek</button>)}
    </div>
  );
}

/**
 * Edycja istniejącego środka kontroli w miejscu (uwaga 17 z 30.07.2026) — do tej pory propozycję AI
 * dało się tylko skasować i wpisać od nowa. Wyczyszczenie daty wysyła `clearDeadline`, bo sam patch
 * nie odróżnia „bez zmian" od „usuń termin".
 */
function ControlEditForm({ ra, h, c, meta, busy, run, onDone }: {
  ra: RiskAssessment; h: RiskHazard; c: RiskControl; meta: RiskMeta | null; busy: boolean;
  run: (p: Promise<RiskAssessment>, onSuccess?: () => void) => void; onDone: () => void;
}) {
  const [type, setType] = useState(c.type);
  const [desc, setDesc] = useState(c.description);
  const [resp, setResp] = useState(c.responsibility);
  const [respOther, setRespOther] = useState(c.responsibilityOther ?? '');
  const [existing, setExisting] = useState(c.existing);
  const [deadline, setDeadline] = useState(c.implementationDeadline ?? '');
  const save = () => run(
    api.updateControl(ra.id, h.id, c.id, {
      type, description: desc, responsibility: resp,
      responsibilityOther: resp === 'INNY' ? respOther : undefined,
      existing,
      implementationDeadline: deadline || undefined,
    }, !deadline && !!c.implementationDeadline),
    onDone,
  );
  return (
    <div className="bg-white border border-water-200 rounded p-2 grid gap-2">
      <div className="grid grid-cols-[140px_1fr] gap-2">
        <SearchableSelect value={type} onChange={v => setType(v as typeof type)} className="btn-sm">
          {(meta?.controlTypes ?? []).map(t => <option key={t.code} value={t.code}>{t.name}</option>)}
        </SearchableSelect>
        <input className="input" value={desc} onChange={e => setDesc(e.target.value)} placeholder="Opis środka kontroli" />
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <SearchableSelect value={resp} onChange={v => setResp(v as typeof resp)} className="btn-sm">
          {(meta?.responsibilities ?? []).map(r => <option key={r.code} value={r.code}>{r.name}</option>)}
        </SearchableSelect>
        {resp === 'INNY' && (
          <input className="input w-56" value={respOther} onChange={e => setRespOther(e.target.value)}
            placeholder="Nazwa podmiotu (np. Gmina Pakosław)" />
        )}
        <label className="flex items-center gap-1 text-[12px]"><input type="checkbox" checked={existing} onChange={e => setExisting(e.target.checked)} /> istniejący</label>
        <input type="date" className="input w-40" value={deadline} onChange={e => setDeadline(e.target.value)} />
        <button onClick={save} disabled={!desc.trim() || busy || (resp === 'INNY' && !respOther.trim())} className="btn btn-primary btn-sm">Zapisz</button>
        <button onClick={onDone} className="btn btn-ghost btn-sm">Anuluj</button>
      </div>
    </div>
  );
}

function AddHazardForm({ ra, meta, busy, run, onDone }: {
  ra: RiskAssessment; meta: RiskMeta | null; busy: boolean; run: (p: Promise<RiskAssessment>, onSuccess?: () => void) => void; onDone: () => void;
}) {
  const [category, setCategory] = useState('MIKROBIOLOGICZNE');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sector, setSector] = useState('');
  const [carrier, setCarrier] = useState('');
  const [event, setEvent] = useState('');
  const [p, setP] = useState(3); const [d, setD] = useState(3);
  const add = () => run(api.addHazard(ra.id, { category, title, description,
    sector: sector || undefined, hazardCarrier: carrier || undefined, triggerEvent: event || undefined,
    probabilityInitial: p, severityInitial: d, probabilityResidual: p, severityResidual: d }), onDone);
  return (
    <div className="card"><div className="card-body grid gap-2">
      <div className="grid grid-cols-[160px_1fr] gap-2">
        <SearchableSelect value={category} onChange={setCategory}>
          {(meta?.hazardCategories ?? []).map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
        </SearchableSelect>
        <input className="input" value={title} onChange={e => setTitle(e.target.value)} placeholder="Tytuł zagrożenia" />
      </div>
      {/* uwaga 23: sekwencja WHO — opis ma wiązać zdarzenie z przyczyną, nie tylko nazywać problem */}
      <textarea className="textarea" value={description} onChange={e => setDescription(e.target.value)}
        placeholder="Opis wg sekwencji „X zdarza się z powodu Y” — np. „Skażenie wody surowej bakteriami zdarza się z powodu nieszczelnego szamba 120 m od studni”" />
      <div className="grid grid-cols-3 gap-2">
        <input className="input" value={sector} onChange={e => setSector(e.target.value)} maxLength={64}
          placeholder="Sektor (np. rolnictwo, transport)" list="hw-sektory" />
        <input className="input" value={carrier} onChange={e => setCarrier(e.target.value)} maxLength={255}
          placeholder="Nośnik zagrożenia (obiekt/działalność)" />
        <input className="input" value={event} onChange={e => setEvent(e.target.value)} maxLength={255}
          placeholder="Zdarzenie niebezpieczne (wyzwalacz)" />
        {/* wartości zgodne z dvgw_sector reguł (V105/V107) — inaczej ten sam sektor tworzy dwie grupy */}
        <datalist id="hw-sektory">
          {['rolnictwo', 'ścieki', 'odpady', 'pochówek', 'przemysł', 'transport', 'górnictwo', 'klimat', 'ochrona']
            .map(s => <option key={s} value={s} />)}
        </datalist>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[12px] text-ink-500">Prawdopod.</span>
        <select value={p} onChange={e => setP(Number(e.target.value))} className="input w-16">{[1, 2, 3, 4, 5].map(n => <option key={n}>{n}</option>)}</select>
        <span className="text-[12px] text-ink-500">Dotkliwość</span>
        <select value={d} onChange={e => setD(Number(e.target.value))} className="input w-16">{[1, 2, 3, 4, 5].map(n => <option key={n}>{n}</option>)}</select>
        <button onClick={add} disabled={!title || busy} className="btn btn-primary btn-sm ml-auto">Dodaj</button>
        <button onClick={onDone} className="btn btn-ghost btn-sm">Anuluj</button>
      </div>
    </div></div>
  );
}

/**
 * Formularz nowego WŁASNEGO zagrożenia wskazanego na mapie — otwiera się po kliknięciu punktu w trybie
 * „Wskaż zagrożenie na mapie". Zapisuje zagrożenie z współrzędnymi (latitude/longitude), które zaraz
 * pojawia się jako punkt na mapie poglądowej.
 */
function PlaceHazardModal({ meta, busy, coord, onSubmit, onClose }: {
  meta: RiskMeta | null; busy: boolean; coord: { lng: number; lat: number };
  onSubmit: (data: Record<string, unknown>) => void; onClose: () => void;
}) {
  const [category, setCategory] = useState('MIKROBIOLOGICZNE');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [p, setP] = useState(3); const [d, setD] = useState(3);
  const submit = () => onSubmit({ category, title, description: description || undefined,
    probabilityInitial: p, severityInitial: d, probabilityResidual: p, severityResidual: d });
  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: { category, title, description, p, d }, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <div className="card w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="card-head flex items-center gap-2">
          <Icon name="map" className="w-4 h-4 text-water-600" />
          <span className="card-title">Nowe zagrożenie na mapie</span>
          <span className="ml-auto text-[11px] font-mono text-ink-400">{coord.lat.toFixed(5)}, {coord.lng.toFixed(5)}</span>
        </div>
        <div className="card-body grid gap-2">
          <div className="grid grid-cols-[160px_1fr] gap-2">
            <SearchableSelect value={category} onChange={setCategory}>
              {(meta?.hazardCategories ?? []).map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </SearchableSelect>
            <input className="input" value={title} onChange={e => setTitle(e.target.value)}
              placeholder="Tytuł zagrożenia" autoFocus onKeyDown={e => { if (e.key === 'Enter' && title && !busy) submit(); }} />
          </div>
          <textarea className="textarea" value={description} onChange={e => setDescription(e.target.value)}
            placeholder="Charakterystyka: co, gdzie i jak może się wydarzyć" />
          <div className="flex items-center gap-2">
            <span className="text-[12px] text-ink-500">Prawdopod.</span>
            <select value={p} onChange={e => setP(Number(e.target.value))} className="input w-16">{[1, 2, 3, 4, 5].map(n => <option key={n}>{n}</option>)}</select>
            <span className="text-[12px] text-ink-500">Dotkliwość</span>
            <select value={d} onChange={e => setD(Number(e.target.value))} className="input w-16">{[1, 2, 3, 4, 5].map(n => <option key={n}>{n}</option>)}</select>
            <button onClick={requestClose} className="btn btn-ghost btn-sm ml-auto">Anuluj</button>
            <button onClick={submit} disabled={!title || busy} className="btn btn-primary btn-sm">Dodaj zagrożenie</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== Monitoring ====================
/** Etykiety badanej matrycy programu badań (element 2 programu wg §9 TrinkwEGV). */
const MONITORING_MATRIX: Record<string, string> = {
  WODA_SUROWA: 'woda surowa', WODA_PODZIEMNA: 'woda podziemna', WODA_POWIERZCHNIOWA: 'woda powierzchniowa',
};

function MonitoringTab({ ra, meta, busy, run, canEdit }: {
  ra: RiskAssessment; meta: RiskMeta | null; busy: boolean; run: (p: Promise<RiskAssessment>, onSuccess?: () => void) => void; canEdit: boolean;
}) {
  const [adding, setAdding] = useState(false);
  const [type, setType] = useState('OPERACYJNY');
  const [kind, setKind] = useState<OperationalKindCode>('BADANIA_LABORATORYJNE');
  const [parameter, setParameter] = useState('');
  const [frequency, setFrequency] = useState('');
  const [samplingPoint, setSamplingPoint] = useState('');
  const [limitValue, setLimitValue] = useState('');
  const [controlId, setControlId] = useState('');

  // uwaga 26: rodzaj monitoringu operacyjnego decyduje o etykietach i o tym, które pola mają sens
  const kinds = meta?.operationalKinds ?? [];
  const kindDef = kinds.find(k => k.code === kind);
  const operacyjny = type === 'OPERACYJNY';
  const parameterLabel = operacyjny ? (kindDef?.parameterLabel ?? 'Parametr') : 'Parametr';
  const pointLabel = operacyjny ? (kindDef?.pointLabel ?? 'Punkt poboru') : 'Punkt poboru';
  const pokazLimit = !operacyjny || (kindDef?.usesLimitValue ?? true);

  // uwaga 24: pozycja wskazuje środek kontroli, którego skuteczność potwierdza — lista z tej oceny
  const controlOptions = useMemo(
    () => ra.hazards.flatMap(h => h.controls.map(c => ({ id: c.id, hazard: h.title, label: c.description }))),
    [ra.hazards],
  );

  const add = () => run(api.addMonitoring(ra.id, {
    type, operationalKind: operacyjny ? kind : undefined,
    parameter, frequency, samplingPoint,
    limitValue: pokazLimit ? limitValue : '',
    controlId: controlId ? Number(controlId) : undefined,
  }), () => {
    setParameter(''); setFrequency(''); setSamplingPoint(''); setLimitValue(''); setControlId(''); setAdding(false);
  });
  return (
    <div className="grid gap-4">
      {ra.monitoring.some(m => m.origin === 'REGULA') && (
        <div className="card border-l-4 border-l-water-400"><div className="card-body text-[12px] text-ink-500 flex items-start gap-2">
          <Icon name="sparkles" className="w-4 h-4 mt-0.5 text-water-600 shrink-0" />
          <span>Pozycje oznaczone <span className="pill pill-water text-[9px]">z ryzyka</span> zostały wyprowadzone
            z wykrytych zagrożeń (art. 4p ust. 1 pkt 10 — zakres i częstotliwość badań wody surowej uwzględniają ocenę
            ryzyka). Kolumna „Dlaczego ten parametr" pokazuje uzasadnienie doboru. Po podpisie oceny pozycje o cyklicznej
            częstotliwości trafiają jako terminy poboru próbek do modułu „Terminy".</span>
        </div></div>
      )}
      {ra.monitoring.length > 0 ? (
        <div className="card"><div className="card-body !p-0 overflow-x-auto"><table className="table-hw">
          <thead><tr><th className="w-32">Typ / rodzaj</th><th>Parametr</th><th className="w-32">Matryca</th><th className="w-36">Częstotliwość</th><th className="w-40">Punkt poboru</th><th className="w-36">Wartość odniesienia</th><th className="w-48">Zagrożenie · środek kontroli</th><th>Dlaczego ten parametr</th><th className="w-8"></th></tr></thead>
          <tbody>{ra.monitoring.map(m => (
            <tr key={m.id}>
              <td className="whitespace-nowrap">
                <span className={'pill ' + (m.type === 'OPERACYJNY' ? 'pill-water' : 'pill-ai')}>{m.typeName}</span>
                {/* uwaga 26: rodzaj operacyjnego pod typem — inspekcja terenowa to nie to samo co badanie */}
                {m.operationalKindName && <div className="text-[10.5px] text-ink-500 mt-0.5">{m.operationalKindName}</div>}
              </td>
              <td className="font-semibold text-ink-800">
                {m.parameter}
                {m.origin === 'REGULA' && <span className="pill pill-water text-[9px] ml-1.5" title="Pozycja wyprowadzona z wykrytych zagrożeń (ryzyko-zależna)">z ryzyka</span>}
                {m.origin === 'RECZNY' && <span className="pill pill-gray text-[9px] ml-1.5">ręczna</span>}
              </td>
              <td className="text-[12px] text-ink-600">{MONITORING_MATRIX[m.matrix ?? ''] ?? m.matrix ?? '—'}</td>
              <td className="text-[12px] text-ink-600">{m.frequency || '—'}</td>
              <td className="text-[12px] text-ink-600">{m.samplingPoint || '—'}</td>
              <td className="text-[12px] font-mono">{m.limitValue || '—'}</td>
              {/* uwaga 24: po co mierzymy ten parametr — które zagrożenie i który środek kontroli */}
              <td className="text-[11px]">
                {m.derivedFromHazardTitle
                  ? <div className="text-ink-600">{m.derivedFromHazardTitle}</div>
                  : <div className="text-ink-300">bez powiązania z zagrożeniem</div>}
                {canEdit ? (
                  <select value={m.controlId ?? ''} disabled={busy}
                    onChange={e => run(api.updateMonitoring(ra.id, m.id, { controlId: Number(e.target.value || 0) }))}
                    className={'mt-1 w-full border rounded px-1 py-0.5 text-[11px] bg-white '
                      + (m.controlId ? 'border-ink-200 text-ink-600' : 'border-amber-300 text-amber-700')}>
                    <option value="">— wskaż środek kontroli —</option>
                    {controlOptions.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                ) : (m.controlDescription
                  ? <div className="text-ink-500 mt-0.5">→ {m.controlDescription}</div>
                  : <div className="text-amber-600 mt-0.5">→ nie wskazano środka kontroli</div>)}
              </td>
              <td className="text-[11px] text-ink-500 max-w-[320px]">{m.justification || (m.origin === 'SZABLON' ? 'Minimum zgodności (biblioteka wzorców per warstwa oceny).' : '—')}</td>
              <td>{canEdit && <button onClick={() => run(api.deleteMonitoring(ra.id, m.id))} disabled={busy} className="btn btn-ghost btn-icon text-ink-400"><Icon name="x" className="w-3.5 h-3.5" /></button>}</td>
            </tr>
          ))}</tbody>
        </table></div></div>
      ) : <div className="card"><div className="card-body text-center text-ink-400 py-6">Brak pozycji monitoringu.</div></div>}

      {canEdit && (adding ? (
        <div className="card"><div className="card-body grid gap-2">
          <div className="grid grid-cols-[160px_200px_1fr] gap-2">
            <SearchableSelect value={type} onChange={setType}>{(meta?.monitoringTypes ?? []).map(t => <option key={t.code} value={t.code}>{t.name}</option>)}</SearchableSelect>
            {operacyjny
              ? <SearchableSelect value={kind} onChange={v => setKind(v as OperationalKindCode)}>
                  {kinds.map(k => <option key={k.code} value={k.code}>{k.name}</option>)}
                </SearchableSelect>
              : <div className="text-[11px] text-ink-400 self-center">monitoring zgodności — bez rodzaju</div>}
            <input className="input" value={parameter} onChange={e => setParameter(e.target.value)} placeholder={parameterLabel} />
          </div>
          <div className={'grid gap-2 ' + (pokazLimit ? 'grid-cols-3' : 'grid-cols-2')}>
            <input className="input" value={frequency} onChange={e => setFrequency(e.target.value)} placeholder="Częstotliwość" />
            <input className="input" value={samplingPoint} onChange={e => setSamplingPoint(e.target.value)} placeholder={pointLabel} />
            {/* wartość odniesienia ma sens przy badaniach i pomiarze ciągłym, nie przy inspekcji terenowej */}
            {pokazLimit && <input className="input" value={limitValue} onChange={e => setLimitValue(e.target.value)} placeholder="Wartość odniesienia" />}
          </div>
          <label className="field">
            <span className="field-label">Potwierdza skuteczność środka kontroli</span>
            <SearchableSelect value={controlId} onChange={setControlId}>
              <option value="">— nie wskazano —</option>
              {controlOptions.map(c => <option key={c.id} value={c.id}>{c.label} ({c.hazard})</option>)}
            </SearchableSelect>
          </label>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setAdding(false)} className="btn btn-ghost btn-sm">Anuluj</button>
            <button onClick={add} disabled={!parameter || busy} className="btn btn-primary btn-sm">Dodaj</button>
          </div>
        </div></div>
      ) : <button onClick={() => setAdding(true)} className="btn btn-secondary btn-sm self-start"><Icon name="plus" className="w-3.5 h-3.5" /> Dodaj pozycję monitoringu</button>)}
    </div>
  );
}

// ==================== Dokumenty źródłowe (upload + indeks AI) ====================
/**
 * Jedno miejsce na wszystkie dokumenty potrzebne do wykonania oceny: drag&drop plików LUB całego folderu,
 * indeksowanie AI (OCR + embed → korpus OCENY_4F, czytane przy generowaniu), lista ze statusem i pobieraniem.
 */
function SourceDocumentsTab({ raId, canEdit, canAI, setErr, onDocsCount }: {
  raId: number; canEdit: boolean; canAI: boolean; setErr: (m: string | null) => void;
  /** Liczba dokumentów wraca na stronę — od niej zależy stan „czeka na dane" w Przeglądzie. */
  onDocsCount: (n: number) => void;
}) {
  const [docs, setDocs] = useState<RiskSourceDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [dragOver, setDragOver] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [scanCount, setScanCount] = useState<number | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [uploadSignal, setUploadSignal] = useState(0);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const folderInput = useRef<HTMLInputElement | null>(null);

  const load = () => {
    setLoading(true);
    api.sourceDocuments(raId).then(d => { setDocs(d); onDocsCount(d.length); })
      .catch(e => setErr(msg(e))).finally(() => setLoading(false));
  };
  useEffect(load, [raId]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (files: File[]) => {
    const clean = files.filter(f => !isSystemJunk(f.name));
    if (!clean.length) { setScanCount(null); return; }
    const sizeErr = validateUploadSize(clean);
    if (sizeErr) { setErr(sizeErr); setScanCount(null); return; }
    setErr(null); setNote(null); setUploadPct(0);
    try {
      const res = await api.uploadSourceDocuments(raId, clean, p => setUploadPct(p.percent));
      const parts = [`Wgrano ${res.attached} z ${clean.length}`];
      if (res.duplicates) parts.push(`${res.duplicates} już było w archiwum`);
      if (res.errors?.length) parts.push(`${res.errors.length} pominięto`);
      setNote(parts.join(' · '));
      if (res.errors?.length) setErr(res.errors.slice(0, 6).join('\n'));
      setDocs(res.documents);
      onDocsCount(res.documents.length);
      if (res.attached > 0) setUploadSignal(s => s + 1);   // → checklist: auto-rozpoznanie typów + odświeżenie
    } catch (e) {
      setErr(msg(e));
    } finally {
      setUploadPct(null); setScanCount(null);
      if (fileInput.current) fileInput.current.value = '';
      if (folderInput.current) folderInput.current.value = '';
    }
  };

  const del = (doc: RiskSourceDocument) => {
    if (!window.confirm(`Odpiąć dokument „${doc.name}” od oceny? Plik pozostanie w archiwum.`)) return;
    api.deleteSourceDocument(raId, doc.id).then(load).catch(e => setErr(msg(e)));
  };

  return (
    <div className="grid gap-4">
      {canEdit && (
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={async e => {
            e.preventDefault(); setDragOver(false);
            if (uploadPct !== null) return;
            setScanCount(0);
            const files = await readDroppedFiles(e.dataTransfer, n => setScanCount(n));
            await upload(files);
          }}
          className={'card border-2 border-dashed transition-colors ' + (dragOver ? 'border-water-500 bg-water-50/40' : 'border-ink-200')}>
          <div className="card-body text-center py-8 grid gap-2">
            <Icon name="upload" className="w-7 h-7 mx-auto text-water-500" />
            <div className="text-[14px] font-semibold text-ink-800">Przeciągnij tu pliki lub cały folder</div>
            <div className="text-[12px] text-ink-500 max-w-[560px] mx-auto">
              PDF, DOCX, XLS(X), obrazy, TXT — do 100&nbsp;MB/plik. System wyciąga tekst (OCR), indeksuje i udostępnia AI przy generowaniu dokumentu oceny.
            </div>
            <div className="flex items-center justify-center gap-2 mt-1">
              <button onClick={() => fileInput.current?.click()} disabled={uploadPct !== null} className="btn btn-secondary btn-sm">
                <Icon name="plus" className="w-3.5 h-3.5" /> Wybierz pliki
              </button>
              <button onClick={() => folderInput.current?.click()} disabled={uploadPct !== null} className="btn btn-secondary btn-sm">
                <Icon name="folder" className="w-3.5 h-3.5" /> Wybierz folder
              </button>
            </div>
            <input ref={fileInput} type="file" multiple className="hidden"
              onChange={e => { const fs = Array.from(e.target.files ?? []); if (fs.length) upload(fs); }} />
            <input ref={folderInput} type="file" multiple className="hidden"
              {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
              onChange={e => { const fs = Array.from(e.target.files ?? []); if (fs.length) upload(fs); }} />
            {scanCount !== null && <div className="text-[12px] text-water-600">Skanuję folder… {scanCount} plików</div>}
            {uploadPct !== null && <UploadProgressBar percent={uploadPct} label="Wysyłanie dokumentów…" compact className="w-full max-w-[560px] mx-auto" />}
            {note && <div className="text-[12px] text-green-600">{note}</div>}
          </div>
        </div>
      )}

      {loading ? (
        <div className="card"><div className="card-body text-center text-ink-400 py-6">Ładowanie…</div></div>
      ) : docs.length === 0 ? (
        <div className="card"><div className="card-body text-center text-ink-400 py-6">
          Brak dokumentów źródłowych.{canEdit ? ' Wgraj pliki powyżej — zostaną odczytane przez AI.' : ''}
        </div></div>
      ) : (
        <div className="card">
          <div className="card-head flex items-center gap-2">
            <span className="card-title">Dokumenty źródłowe</span>
            <span className="text-[11px] text-ink-400">{docs.length} · czytane przez AI przy generowaniu dokumentu</span>
          </div>
          <div className="card-body !p-0 overflow-x-auto"><table className="table-hw">
            <thead><tr>
              <th>Nazwa</th><th className="w-24">Rozmiar</th>
              <th className="w-32 text-center">AI / indeks</th><th className="w-28">Wgrano</th><th className="w-20"></th>
            </tr></thead>
            <tbody>{docs.map(d => (
              <tr key={d.id}>
                <td className="font-medium text-ink-800">
                  <a href={api.sourceDocumentDownloadUrl(raId, d.id)} target="_blank" rel="noreferrer" className="text-water-700 hover:underline">{d.name}</a>
                  {d.pages != null && <span className="text-ink-400 text-[11px] ml-1.5">{d.pages} str.</span>}
                </td>
                <td className="text-[12px] text-ink-500">{d.sizeBytes != null ? fmtBytes(d.sizeBytes) : '—'}</td>
                <td className="text-center">
                  {d.indexed && d.ragEnabled
                    ? <span className="pill pill-green text-[10px]" title={d.textChars != null ? `${d.textChars} znaków tekstu` : undefined}>zaindeksowany</span>
                    : <span className="pill pill-gray text-[10px]" title="Tekst nie został wyekstrahowany — plik nie będzie czytany przez AI">bez indeksu</span>}
                </td>
                <td className="text-[12px] text-ink-500">{d.createdAt?.slice(0, 10)}</td>
                <td className="text-right whitespace-nowrap">
                  <a href={api.sourceDocumentDownloadUrl(raId, d.id)} target="_blank" rel="noreferrer" className="btn btn-ghost btn-icon text-ink-400" title="Pobierz"><Icon name="download" className="w-3.5 h-3.5" /></a>
                  {canEdit && <button onClick={() => del(d)} className="btn btn-ghost btn-icon text-ink-400" title="Odepnij od oceny"><Icon name="x" className="w-3.5 h-3.5" /></button>}
                </td>
              </tr>
            ))}</tbody>
          </table></div>
        </div>
      )}

      <RequiredDocsCard raId={raId} canEdit={canEdit} canAI={canAI} attachedDocs={docs}
        uploadSignal={uploadSignal} setErr={setErr} />
    </div>
  );
}

const REQ_STATUS: { code: RiskDocStatusCode; name: string; cls: string }[] = [
  { code: 'PENDING', name: 'Oczekuje', cls: 'pill-gray' },
  { code: 'RECEIVED', name: 'Mamy', cls: 'pill-green' },
  { code: 'NOT_APPLICABLE', name: 'Nie dotyczy', cls: 'pill-gray' },
  { code: 'WAIVED', name: 'Zwolniono', cls: 'pill-amber' },
];

/**
 * Checklista wymaganych dokumentów oceny (art. 4f) + przegląd propozycji AI rozpoznania typu.
 * Po uploadzie automatycznie odpala rozpoznanie typów (gdy AI dostępne). „Mamy/brakuje", statusy, pliki.
 */
function RequiredDocsCard({ raId, canEdit, canAI, attachedDocs, uploadSignal, setErr }: {
  raId: number; canEdit: boolean; canAI: boolean;
  attachedDocs: RiskSourceDocument[]; uploadSignal: number; setErr: (m: string | null) => void;
}) {
  const [checklist, setChecklist] = useState<RiskDocChecklist | null>(null);
  const [proposals, setProposals] = useState<RiskDocProposal[]>([]);
  const [override, setOverride] = useState<Record<number, number | ''>>({});
  const [busy, setBusy] = useState(false);
  const [classifying, setClassifying] = useState(false);

  const load = () => {
    api.requiredDocuments(raId).then(setChecklist).catch(e => setErr(msg(e)));
    api.documentProposals(raId).then(setProposals).catch(() => { /* propozycje opcjonalne */ });
  };
  useEffect(load, [raId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Po uploadzie: automatyczne rozpoznanie typów (jeśli AI dostępne) + odświeżenie checklisty.
  useEffect(() => {
    if (uploadSignal === 0) return;
    if (canAI) {
      setClassifying(true);
      api.classifyDocuments(raId).then(setProposals).catch(e => setErr(msg(e)))
        .finally(() => { setClassifying(false); api.requiredDocuments(raId).then(setChecklist).catch(() => {}); });
    } else {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadSignal]);

  const targetOf = (p: RiskDocProposal): number | undefined => {
    const o = override[p.id];
    if (o !== undefined) return o === '' ? undefined : o;   // jawny wybór użytkownika (w tym „nie przypisuj")
    return p.proposedItemId ?? undefined;
  };
  const accept = (p: RiskDocProposal) => {
    setBusy(true); setErr(null);
    api.acceptProposal(raId, p.id, targetOf(p)).then(() => load()).catch(e => setErr(msg(e))).finally(() => setBusy(false));
  };
  const reject = (p: RiskDocProposal) => {
    setBusy(true);
    api.rejectProposal(raId, p.id).then(() => setProposals(ps => ps.filter(x => x.id !== p.id))).catch(e => setErr(msg(e))).finally(() => setBusy(false));
  };
  const setStatus = (rid: number, status: RiskDocStatusCode) => {
    let note: string | undefined;
    if (status === 'WAIVED') { const r = window.prompt('Uzasadnienie zwolnienia:'); if (r == null) return; note = r; }
    setBusy(true);
    api.changeReqDocStatus(raId, rid, status, note).then(() => load()).catch(e => setErr(msg(e))).finally(() => setBusy(false));
  };
  const link = (rid: number, docId: number) => {
    setBusy(true);
    api.linkReqDocFile(raId, rid, docId).then(() => load()).catch(e => setErr(msg(e))).finally(() => setBusy(false));
  };
  const unlink = (rid: number, docId: number) => {
    setBusy(true);
    api.unlinkReqDocFile(raId, rid, docId).then(() => load()).catch(e => setErr(msg(e))).finally(() => setBusy(false));
  };
  const classify = () => {
    setClassifying(true); setErr(null);
    api.classifyDocuments(raId).then(setProposals).catch(e => setErr(msg(e))).finally(() => setClassifying(false));
  };
  const refresh = () => { setBusy(true); api.refreshReqDocs(raId).then(() => load()).catch(e => setErr(msg(e))).finally(() => setBusy(false)); };

  if (!checklist) return null;
  const mandatoryMissing = checklist.items.filter(i => i.mandatory && i.status === 'PENDING').length;
  const open = proposals.filter(p => p.status === 'PROPOSED');

  return (
    <>
      {open.length > 0 && (
        <div className="card border-l-4 border-l-ai-400">
          <div className="card-head flex items-center gap-2">
            <Icon name="sparkles" className="w-4 h-4 text-ai-500" />
            <span className="card-title">AI rozpoznało typ dokumentów — do zatwierdzenia ({open.length})</span>
          </div>
          <div className="card-body grid gap-2">
            {open.map(p => (
              <div key={p.id} className="flex items-center gap-2 flex-wrap text-[13px] border-b border-ink-50 pb-2 last:border-0">
                <span className="font-medium text-ink-800 min-w-[160px] max-w-[240px] truncate" title={p.archiveDocumentName}>{p.archiveDocumentName}</span>
                <Icon name="arrow" className="w-3.5 h-3.5 text-ink-300 shrink-0" />
                <SearchableSelect value={String(override[p.id] ?? p.proposedItemId ?? '')} className="btn-sm min-w-[220px]"
                  onChange={v => setOverride(m => ({ ...m, [p.id]: v ? Number(v) : '' }))}>
                  <option value="">— nie przypisuj —</option>
                  {checklist.items.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                </SearchableSelect>
                {p.confidence != null && <span className="text-[11px] text-ink-400">{Math.round(p.confidence * 100)}%{p.source ? ` · ${p.source}` : ''}</span>}
                {canEdit && <button onClick={() => accept(p)} disabled={busy || !targetOf(p)} className="btn btn-primary btn-sm">Zatwierdź</button>}
                {canEdit && <button onClick={() => reject(p)} disabled={busy} className="btn btn-ghost btn-sm">Odrzuć</button>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-head flex items-center gap-2 flex-wrap">
          <span className="card-title">Wymagane dokumenty</span>
          <span className="text-[12px] text-ink-500">
            {checklist.received}/{checklist.total} mamy{mandatoryMissing > 0 ? ` · brakuje ${mandatoryMissing} obowiązkowych` : ''}
          </span>
          <div className="ml-auto flex items-center gap-2">
            {canEdit && canAI && <button onClick={classify} disabled={classifying || busy} className="btn btn-ai btn-sm"><Icon name="sparkles" className="w-3.5 h-3.5" /> {classifying ? 'Rozpoznaję…' : 'Rozpoznaj typy (AI)'}</button>}
            {canEdit && <button onClick={refresh} disabled={busy} className="btn btn-ghost btn-sm" title="Dodaj brakujące pozycje z katalogu">Odśwież katalog</button>}
          </div>
        </div>
        <div className="card-body !p-0 overflow-x-auto"><table className="table-hw">
          <thead><tr><th>Dokument</th><th className="w-40">Status</th><th>Pliki</th></tr></thead>
          <tbody>{checklist.items.map(i => {
            const linkable = attachedDocs.filter(d => !i.files.some(f => f.id === d.id));
            return (
              <tr key={i.id} className="align-top">
                <td>
                  <div className="font-medium text-ink-800">{i.name}{i.mandatory && <span className="text-red-500 ml-1" title="Obowiązkowy">*</span>}</div>
                  {i.legalBasis && <div className="text-[11px] text-ink-400">{i.legalBasis}</div>}
                  {i.note && <div className="text-[11px] text-ink-500 mt-0.5 italic">{i.note}</div>}
                </td>
                <td>
                  {canEdit
                    ? <SearchableSelect value={i.status} className="btn-sm" onChange={v => v && setStatus(i.id, v as RiskDocStatusCode)}>
                        {REQ_STATUS.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                      </SearchableSelect>
                    : <span className={'pill ' + (REQ_STATUS.find(s => s.code === i.status)?.cls ?? 'pill-gray')}>{i.statusName}</span>}
                </td>
                <td>
                  <div className="flex flex-col gap-1">
                    {i.files.map(f => (
                      <span key={f.id} className="inline-flex items-center gap-1 text-[12px]">
                        <a href={api.sourceDocumentDownloadUrl(raId, f.id)} target="_blank" rel="noreferrer" className="text-water-700 hover:underline truncate max-w-[220px]">{f.name}</a>
                        {canEdit && <button onClick={() => unlink(i.id, f.id)} className="text-ink-300 hover:text-red-600" title="Odepnij plik">×</button>}
                      </span>
                    ))}
                    {canEdit && linkable.length > 0 && (
                      <SearchableSelect value="" className="btn-sm mt-1 min-w-[200px]" onChange={v => v && link(i.id, Number(v))}>
                        <option value="">+ podepnij wgrany plik…</option>
                        {linkable.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </SearchableSelect>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}</tbody>
        </table></div>
      </div>
    </>
  );
}

// ==================== Dokument ====================
function DocumentTab({ ra, busy, run, reload, setRa, setErr, canEdit, canAI, canSign }: {
  ra: RiskAssessment; busy: boolean; run: (p: Promise<RiskAssessment>, onSuccess?: () => void) => void; reload: () => void;
  setRa: (r: RiskAssessment) => void; setErr: (m: string | null) => void; canEdit: boolean; canAI: boolean; canSign: boolean;
}) {
  return (
    <div className="grid gap-4">
      <div className="card"><div className="card-body flex items-center gap-2 flex-wrap">
        <a href={api.pdfUrl(ra.id)} target="_blank" rel="noreferrer" className="btn btn-primary btn-sm"><Icon name="file-text" className="w-3.5 h-3.5" /> Podgląd PDF dokumentu</a>
        <a href={api.exportGeoJsonUrl(ra.id)} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm"
          title="FeatureCollection WGS84: ujęcie, obszar zasilania, strefy ochronne, presje — do przekazania z raportem">
          <Icon name="map" className="w-3.5 h-3.5" /> Pakiet geodanych (GeoJSON)</a>
        {canSign && (ra.status === 'RECENZJA' || ra.status === 'DO_RECENZJI') && (
          <button onClick={() => { if (window.confirm('Podpisać i sfinalizować dokument?')) run(api.sign(ra.id)); }} disabled={busy} className="btn btn-water btn-sm"><Icon name="check" className="w-3.5 h-3.5" /> Zatwierdź i podpisz</button>
        )}
        <span className="text-[12px] text-ink-400 ml-auto">Sekcje narracyjne redagowane poniżej trafiają do PDF; tabele zagrożeń, macierz i monitoring generują się automatycznie.</span>
      </div></div>

      {ra.sections.some(s => s.stale) && (
        <div className="card border-l-4 border-l-amber-400"><div className="card-body flex items-start gap-2 text-[13px]">
          <Icon name="bell" className="w-4 h-4 mt-0.5 text-amber-500 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-ink-900">
              Rozdziały wymagające aktualizacji: {ra.sections.filter(s => s.stale).length}
            </div>
            <div className="text-ink-500 mt-0.5">
              Dane źródłowe (zagrożenia, ocena ryzyka, środki kontroli lub monitoring) zmieniły się po wygenerowaniu
              treści. Zregeneruj nieaktualne rozdziały albo zredaguj je ręcznie, aby raport był spójny ze źródłem.
            </div>
          </div>
          {canEdit && canAI && (
            <button onClick={() => run(api.regenerateStaleSections(ra.id))} disabled={busy}
              className="btn btn-ai btn-sm shrink-0"><Icon name="sparkles" className="w-3.5 h-3.5" /> Regeneruj nieaktualne</button>
          )}
        </div></div>
      )}

      {ra.documents.length > 0 && (
        <div className="card"><div className="card-head"><span className="card-title">Wersje dokumentu</span></div>
          <div className="card-body !p-0"><table className="table-hw">
            <thead><tr><th className="w-16">Wersja</th><th>Plik</th><th className="w-24">Podpis</th><th className="w-40">Data</th><th className="w-20"></th></tr></thead>
            <tbody>{ra.documents.map(d => (
              <tr key={d.id}>
                <td className="font-mono">{d.version}</td>
                <td className="text-[12px]">{d.fileName}<div className="text-ink-400 text-[10px] font-mono">SHA-256 {d.sha256?.slice(0, 16)}…</div></td>
                <td>{d.signed ? <span className="pill pill-green"><span className="pill-dot" />podpisany</span> : <span className="pill pill-gray">szkic</span>}</td>
                <td className="text-[12px] text-ink-500">{d.signedAt ? `${d.signerName ?? ''} · ${d.signedAt.slice(0, 10)}` : d.createdAt.slice(0, 10)}</td>
                <td><a href={api.downloadUrl(ra.id, d.id)} className="btn btn-ghost btn-sm text-water-600"><Icon name="download" className="w-3.5 h-3.5" /></a></td>
              </tr>
            ))}</tbody>
          </table></div>
        </div>
      )}

      <div className="grid gap-3">
        {ra.sections.map(s => (
          <SectionEditor key={s.id} ra={ra} section={s} busy={busy} run={run} setRa={setRa} reload={reload} setErr={setErr} canEdit={canEdit} canAI={canAI} />
        ))}
      </div>
    </div>
  );
}

function SectionEditor({ ra, section, busy, run, setRa, reload, setErr, canEdit, canAI }: {
  ra: RiskAssessment; section: RiskNarrativeSection; busy: boolean; run: (p: Promise<RiskAssessment>, onSuccess?: () => void) => void;
  setRa: (r: RiskAssessment) => void; reload: () => void; setErr: (m: string | null) => void; canEdit: boolean; canAI: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [html, setHtml] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      const src = section.editedText && section.editedText.trim()
        ? section.editedText
        : (section.draftText ? markdownToHtml(section.draftText) : '');
      setHtml(src);
    }
    // eslint-disable-next-line
  }, [open, section.id, section.status]);

  const gen = () => {
    setSaving(true); setErr(null);
    api.generateSection(ra.id, section.id).then(reload).catch(e => setErr(msg(e))).finally(() => setSaving(false));
  };
  const save = (accept: boolean) => {
    setSaving(true); setErr(null);
    api.saveSection(ra.id, section.id, html, accept).then(reload).catch(e => setErr(msg(e))).finally(() => setSaving(false));
  };

  return (
    <div className="card">
      <div className="card-head cursor-pointer" onClick={() => setOpen(o => !o)}>
        <Icon name="chevron" className={'w-3.5 h-3.5 transition-transform ' + (open ? 'rotate-90' : '')} />
        <span className="card-title flex-1">{section.sortOrder + 1}. {section.section}</span>
        {section.stale && (
          <span className="pill pill-amber text-[10px]" title="Dane źródłowe zmieniły się po wygenerowaniu tej sekcji — treść może być niespójna">
            wymaga aktualizacji
          </span>
        )}
        <span className={'pill pill-' + section.statusColor}>{section.statusName}</span>
      </div>
      {open && (
        <div className="card-body grid gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {canEdit && canAI && <button onClick={gen} disabled={saving || busy} className="btn btn-ai btn-sm"><Icon name="sparkles" className="w-3.5 h-3.5" /> {saving ? 'Generuję…' : 'Generuj'}</button>}
            {section.model && <span className="text-[10px] text-ink-400">model: {section.model}</span>}
          </div>
          {section.draftText && (
            <details className="text-[12px]">
              <summary className="cursor-pointer text-ink-400">Szkic AI (surowy)</summary>
              <div className="whitespace-pre-wrap text-ink-500 mt-1 p-2 bg-ink-50 rounded max-h-48 overflow-auto">{section.draftText}</div>
            </details>
          )}
          <TiptapEditor value={html} onChange={setHtml} minHeight="180px" readOnly={!canEdit} />
          {canEdit && (
            <div className="flex gap-2 justify-end">
              <button onClick={() => save(false)} disabled={saving || busy} className="btn btn-secondary btn-sm">Zapisz</button>
              <button onClick={() => save(true)} disabled={saving || busy} className="btn btn-primary btn-sm"><Icon name="check" className="w-3.5 h-3.5" /> Zapisz i zaakceptuj</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== small helpers ====================
function Kpi({ label, value, accent }: { label: string; value: number | string; accent?: string }) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className={'kpi-value ' + (accent ?? '')}>{value}</div>
    </div>
  );
}
function KV({ k, v }: { k: string; v?: string | null }) {
  return (<><span className="text-ink-400">{k}</span><span className="text-ink-800">{v || '—'}</span></>);
}
function msg(e: unknown): string {
  if (e && typeof e === 'object' && 'message' in e) return String((e as { message: unknown }).message);
  return String(e);
}
